<template>
    <div class="communications-inbox">
        <v-card class="inbox-root-card">
            <v-card-title class="inbox-header white--text px-3 pb-3">
                <v-icon color="white" class="mr-2">mdi-inbox</v-icon>
                Communications Inbox
                <v-spacer></v-spacer>
                <span class="inbox-live caption mr-3">
                    <span class="inbox-live-dot"></span>
                    Live
                </span>
                <v-btn
                    outline
                    color="white"
                    small
                    class="inbox-refresh-btn"
                    :loading="loading"
                    @click="loadInbox"
                >
                    <v-icon small class="mr-1">mdi-refresh</v-icon>
                    Refresh
                </v-btn>
            </v-card-title>

            <div class="inbox-canvas">
                <v-alert
                    :value="!!errorMessage"
                    type="error"
                    @input="errorMessage = ''"
                >
                    {{ errorMessage }}
                </v-alert>

                <div class="inbox-chip-row inbox-panel">
                    <div class="inbox-chip-group">
                        <v-chip
                            small
                            text-color="white"
                            :color="filters.assignedFilter === 'mineAndUnassigned' ? '#4caf50' : '#b0bec5'"
                            class="inbox-chip"
                            @click.native="setAssignedFilter('mineAndUnassigned')"
                        >
                            My Queue ({{ counts.all }})
                        </v-chip>
                        <v-chip
                            small
                            text-color="white"
                            :color="filters.assignedFilter === 'mine' ? '#4caf50' : '#b0bec5'"
                            class="inbox-chip"
                            @click.native="setAssignedFilter('mine')"
                        >
                            Assigned to Me ({{ counts.assignedToMe }})
                        </v-chip>
                        <v-chip
                            small
                            text-color="white"
                            :color="filters.assignedFilter === 'unassigned' ? '#fdb400' : '#b0bec5'"
                            class="inbox-chip"
                            @click.native="setAssignedFilter('unassigned')"
                        >
                            Unassigned ({{ counts.unassigned }})
                        </v-chip>
                        <v-chip
                            small
                            text-color="white"
                            :color="onlyNeedsAccount ? '#e53935' : '#b0bec5'"
                            class="inbox-chip"
                            @click.native="onlyNeedsAccount = !onlyNeedsAccount"
                        >
                            Unknown account ({{ liveCounts.needsAccount }})
                        </v-chip>
                    </div>
                    <div class="inbox-chip-group inbox-chip-group--types">
                        <v-chip
                            v-for="typeOption in typeOptions"
                            :key="typeOption.value"
                            small
                            text-color="white"
                            :color="filters.types.includes(typeOption.value) ? typeColor(typeOption.value) : '#cfd8dc'"
                            class="inbox-chip"
                            @click.native="toggleType(typeOption.value)"
                        >
                            {{ typeOption.label }} ({{ counts[typeOption.countKey] }})
                        </v-chip>
                    </div>
                </div>

                <v-card outline class="inbox-filter-bar pa-2">
                    <div class="inbox-filter-toolbar">
                        <div class="inbox-filter-field inbox-filter-field--search">
                            <v-text-field
                                v-model="filters.search"
                                placeholder="Search name, account, phone, subject, transcript"
                                prepend-inner-icon="mdi-magnify"
                                solo
                                flat
                                dense
                                hide-details
                                clearable
                                background-color="#fff"
                            ></v-text-field>
                        </div>
                        <div class="inbox-filter-field inbox-filter-field--assigned">
                            <v-select
                                v-model="filters.assignedFilter"
                                :items="visibleAssignedFilterOptions"
                                prepend-inner-icon="mdi-account-check"
                                solo
                                flat
                                dense
                                hide-details
                                background-color="#fff"
                                @change="loadInbox"
                            ></v-select>
                        </div>
                        <div v-if="filters.assignedFilter === 'user'" class="inbox-filter-field">
                            <v-autocomplete
                                v-model="filters.assignedToUser"
                                placeholder="User..."
                                prepend-inner-icon="mdi-account"
                                solo
                                flat
                                dense
                                hide-details
                                hide-no-data
                                :items="users"
                                :search-input.sync="loadUsers"
                                :menu-props="userMenuProps"
                                background-color="#fff"
                                @focus="ensureUsersLoaded"
                                @change="loadInbox"
                            ></v-autocomplete>
                        </div>
                        <div class="inbox-filter-field">
                            <v-select
                                v-model="filters.status"
                                :items="statusOptions"
                                prepend-inner-icon="mdi-flag-outline"
                                solo
                                flat
                                dense
                                hide-details
                                background-color="#fff"
                                @change="loadInbox"
                            ></v-select>
                        </div>
                        <div class="inbox-filter-field inbox-filter-field--daterange">
                            <v-menu
                                v-model="fromDateMenu"
                                :close-on-content-click="true"
                                offset-y
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="filters.fromDate"
                                        :placeholder="usesDefaultDateRange ? 'Last 30 days' : 'From'"
                                        prepend-inner-icon="mdi-calendar"
                                        readonly
                                        solo
                                        flat
                                        dense
                                        hide-details
                                        clearable
                                        background-color="#fff"
                                        v-on="on"
                                        @click:clear="filters.fromDate = ''; loadInbox()"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="filters.fromDate"
                                    @input="fromDateMenu = false; loadInbox()"
                                ></v-date-picker>
                            </v-menu>
                            <span class="inbox-filter-daterange-sep">&ndash;</span>
                            <v-menu
                                v-model="toDateMenu"
                                :close-on-content-click="true"
                                offset-y
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="filters.toDate"
                                        placeholder="To"
                                        prepend-inner-icon="mdi-calendar"
                                        readonly
                                        solo
                                        flat
                                        dense
                                        hide-details
                                        clearable
                                        background-color="#fff"
                                        v-on="on"
                                        @click:clear="filters.toDate = ''; loadInbox()"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="filters.toDate"
                                    @input="toDateMenu = false; loadInbox()"
                                ></v-date-picker>
                            </v-menu>
                        </div>
                        <div class="inbox-filter-field">
                            <v-select
                                v-model="sortBy"
                                :items="sortOptions"
                                prepend-inner-icon="mdi-sort"
                                solo
                                flat
                                dense
                                hide-details
                                background-color="#fff"
                                @change="loadInbox"
                            ></v-select>
                        </div>
                    </div>
                </v-card>

                <v-progress-linear
                    v-if="loading"
                    :indeterminate="true"
                    color="success"
                    class="mb-2"
                ></v-progress-linear>

                <v-layout row wrap class="inbox-split">
                    <v-flex xs12 md5 class="inbox-split-list">
                        <v-layout
                            v-if="selectedIds.length && !historyDialog"
                            row
                            align-center
                            class="inbox-bulk-bar mb-2"
                        >
                            <span class="inbox-bulk-count">
                                {{ selectedIds.length }} selected
                            </span>
                            <v-btn
                                small
                                color="success"
                                :loading="bulkProcessing"
                                @click="bulkPickup"
                            >
                                Pickup Selected
                            </v-btn>
                            <v-btn
                                small
                                color="success"
                                :loading="bulkProcessing"
                                @click="bulkUnassign"
                            >
                                Unassign Selected
                            </v-btn>
                            <v-btn
                                small
                                color="success"
                                :loading="bulkProcessing"
                                @click="openBulkUpdateDialog"
                            >
                                Update Selected
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn small flat @click="clearSelection">Clear</v-btn>
                        </v-layout>
                        <div class="inbox-list-card">
                            <div class="inbox-list-count">
                                <span
                                    v-if="usesDefaultDateRange"
                                    class="inbox-list-count-range"
                                >
                                    Showing last 30 days
                                </span>
                                <span class="inbox-list-count-value">{{ inboxRangeLabel }}</span>
                            </div>
                            <div class="inbox-row-grid inbox-row-grid--header">
                                <div class="inbox-row-checkbox">
                                    <v-checkbox
                                        :input-value="allFilteredSelected"
                                        :indeterminate="selectedIds.length > 0 && !allFilteredSelected"
                                        color="success"
                                        hide-details
                                        @change="toggleSelectAll"
                                    ></v-checkbox>
                                </div>
                                <div>Type</div>
                                <div>Account</div>
                                <div>Assigned</div>
                                <div>30d</div>
                                <div>Time</div>
                            </div>
                            <div class="inbox-list" ref="inboxList" @scroll="onInboxListScroll">
                                <div
                                    v-if="!loading && !loadingMore && !filteredItems.length"
                                    class="grey--text text-xs-center py-4"
                                >
                                    No communications match your filters
                                </div>
                                <v-card
                                    v-for="item in filteredItems"
                                    :key="item.communicationID"
                                    class="inbox-item"
                                    :class="{
                                        'inbox-item--active':
                                            selected &&
                                            selected.communicationID ===
                                                item.communicationID,
                                    }"
                                    @click.native="selectItem(item)"
                                >
                                    <div class="inbox-row-grid">
                                        <div class="inbox-row-checkbox" @click.stop>
                                            <v-checkbox
                                                :input-value="isRowSelected(item)"
                                                color="success"
                                                hide-details
                                                @change="toggleRowSelection(item)"
                                            ></v-checkbox>
                                        </div>
                                        <div class="inbox-row-type">
                                            <v-icon small :color="typeColor(item.type)">
                                                {{ typeIcon(item.type) }}
                                            </v-icon>
                                            <v-icon
                                                v-if="item.status === 'COMPLETED'"
                                                class="inbox-row-completed-icon"
                                                color="#4caf50"
                                                title="Completed"
                                            >
                                                mdi-check-circle
                                            </v-icon>
                                        </div>
                                        <div class="inbox-row-account">
                                            <div class="inbox-row-name">
                                                <span class="inbox-row-name-en">
                                                    {{ inboxItemName(item) }}
                                                </span>
                                                <span v-if="item.nameH" class="inbox-row-name-he">
                                                    {{ item.nameH }}
                                                </span>
                                                <span
                                                    v-if="item.attachmentCount"
                                                    class="inbox-row-attach"
                                                    :title="item.attachmentCount + ' attachment(s)'"
                                                >
                                                    <v-icon small color="#607d8b">mdi-paperclip</v-icon>
                                                    {{ item.attachmentCount }}
                                                </span>
                                            </div>
                                            <div class="inbox-row-preview">
                                                {{ previewText(item) }}
                                            </div>
                                        </div>
                                        <div class="inbox-row-assigned">
                                            <v-chip
                                                small
                                                text-color="white"
                                                :color="assignedChipColor(item.assignedTo)"
                                            >
                                                {{ assignedChipLabel(item.assignedTo) }}
                                            </v-chip>
                                            <v-chip
                                                v-if="item.needsAccount"
                                                small
                                                text-color="white"
                                                color="#e53935"
                                                class="mt-1"
                                            >
                                                Unknown account
                                            </v-chip>
                                            <v-chip
                                                v-if="item.isNewStudent"
                                                small
                                                text-color="white"
                                                color="#1e88e5"
                                                class="mt-1"
                                            >
                                                New Student
                                            </v-chip>
                                        </div>
                                        <div class="inbox-row-count">
                                            <button
                                                v-if="item.communicationCount !== null && item.communicationCount !== undefined"
                                                type="button"
                                                class="inbox-comm-count inbox-comm-count--grid"
                                                title="Communications from this account in the last 30 days"
                                                @click.stop="openCommunicationHistory(item)"
                                            >
                                                {{ item.communicationCount }}
                                            </button>
                                        </div>
                                        <div class="inbox-row-time">
                                            <span class="inbox-row-date">{{ formatListDate(item.date) }}</span>
                                            <span class="inbox-row-clock">{{ formatListTime(item.date) }}</span>
                                        </div>
                                    </div>
                                </v-card>
                                <div v-if="loadingMore" class="grey--text text-xs-center py-3">
                                    Loading more...
                                </div>
                            </div>
                        </div>
                    </v-flex>
                    <v-flex xs12 md7 class="inbox-split-detail">
                        <v-card v-if="!selected" class="inbox-detail-empty pa-4 grey--text text-xs-center">
                            Select a communication to review.
                        </v-card>
                        <v-card v-else class="inbox-detail">
                            <div
                                class="inbox-detail-sticky"
                                :class="{ 'inbox-detail-sticky--collapsed': contextPanelCollapsed }"
                            >
                                <div
                                    class="inbox-account-box"
                                    :class="{ 'inbox-account-box--missing': selected.needsAccount }"
                                >
                                    <div v-if="!selected.needsAccount" class="inbox-account-box-inner">
                                        <div class="inbox-account-box-info">
                                            <div class="inbox-account-name">
                                                <v-icon
                                                    small
                                                    class="mr-2"
                                                    :color="typeColor(selected.type)"
                                                >
                                                    {{ typeIcon(selected.type) }}
                                                </v-icon>
                                                <span class="inbox-account-name-en">
                                                    {{ selected.name || selected.accountID }}
                                                </span>
                                                <span v-if="selected.nameH" class="inbox-account-name-he">
                                                    {{ selected.nameH }}
                                                </span>
                                            </div>
                                            <div class="inbox-account-meta">
                                                Account {{ selected.accountID }}
                                                <span v-if="selected.emailAddress"> &middot; {{ selected.emailAddress }}</span>
                                            </div>
                                            <div
                                                v-if="selected.address"
                                                class="inbox-account-meta inbox-account-address"
                                                :class="{
                                                    'inbox-account-address--collapsed':
                                                        contextPanelCollapsed,
                                                }"
                                            >
                                                <v-icon small color="#b0bec5" class="mr-1">mdi-map-marker</v-icon>
                                                {{ selected.address }}
                                            </div>
                                        </div>
                                        <div class="inbox-account-box-actions">
                                            <v-btn
                                                outline
                                                small
                                                class="inbox-context-toggle"
                                                @click="toggleContextPanel"
                                            >
                                                <v-icon small class="mr-1">
                                                    {{
                                                        contextPanelCollapsed
                                                            ? "mdi-chevron-down"
                                                            : "mdi-chevron-up"
                                                    }}
                                                </v-icon>
                                                {{ contextPanelCollapsed ? "Expand" : "Minimize" }}
                                            </v-btn>
                                            <v-btn
                                                outline
                                                small
                                                class="inbox-unlink-btn"
                                                :loading="unlinkingAccount"
                                                @click="openUnlinkAccountDialog"
                                            >
                                                <v-icon small class="mr-1">mdi-link-variant-off</v-icon>
                                                Unlink account
                                            </v-btn>
                                        </div>
                                    </div>
                                    <div v-else class="inbox-account-box-inner">
                                        <div class="inbox-account-missing-text">
                                            <v-icon small color="#ffcc80" class="mr-1">mdi-account-alert</v-icon>
                                            {{ inboxItemName(selected) }}
                                        </div>
                                        <v-btn
                                            outline
                                            small
                                            class="inbox-context-toggle"
                                            @click="toggleContextPanel"
                                        >
                                            <v-icon small class="mr-1">
                                                {{
                                                    contextPanelCollapsed
                                                        ? "mdi-chevron-down"
                                                        : "mdi-chevron-up"
                                                }}
                                            </v-icon>
                                            {{ contextPanelCollapsed ? "Expand" : "Minimize" }}
                                        </v-btn>
                                    </div>
                                </div>

                                <div
                                    v-if="selected.needsAccount && !contextPanelCollapsed"
                                    class="inbox-link-box"
                                >
                                    <div class="inbox-link-title">
                                        <v-icon small color="#e65100" class="mr-1">mdi-alert-circle</v-icon>
                                        Link to account
                                        <span
                                            v-if="selected.type === 'EMAIL' || selected.type === 'FAX' || selected.type === 'VOICEMAIL'"
                                            class="inbox-link-hint"
                                        >
                                            Search the parent account, or the student on the form
                                        </span>
                                    </div>
                                    <div class="inbox-link-row">
                                        <assign-account-lookup
                                            ref="assignLookup"
                                            v-model="assignAccountID"
                                            class="inbox-link-lookup"
                                            @selected="onAssignAccountSelected"
                                        ></assign-account-lookup>
                                        <assign-student-lookup
                                            v-if="selected.type === 'EMAIL' || selected.type === 'FAX' || selected.type === 'VOICEMAIL'"
                                            ref="studentLookup"
                                            v-model="assignAccountID"
                                            class="inbox-link-lookup"
                                            @selected="onAssignStudentSelected"
                                        ></assign-student-lookup>
                                        <v-btn
                                            color="success"
                                            small
                                            class="inbox-link-btn"
                                            :loading="processing"
                                            :disabled="!assignAccountID"
                                            @click="assignAccount"
                                        >
                                            <v-icon small class="mr-1">mdi-link-variant</v-icon>
                                            Link account
                                        </v-btn>
                                    </div>
                                </div>

                                <div
                                    v-if="!selected.needsAccount"
                                    class="inbox-reg-box"
                                >
                                    <div class="inbox-reg-header">
                                        <div class="inbox-reg-title">
                                            <v-icon small color="#2e7d32" class="mr-1">mdi-clipboard-text</v-icon>
                                            Registration
                                        </div>
                                        <v-chip
                                            v-if="selected.registrationStatus"
                                            small
                                            text-color="white"
                                            :color="regStatusColor(selected.registrationStatus)"
                                        >
                                            <v-icon small color="white" class="mr-1">
                                                {{ regStatusIcon(selected.registrationStatus) }}
                                            </v-icon>
                                            {{ selected.registrationStatus }}
                                        </v-chip>
                                        <span
                                            v-if="contextPanelCollapsed && incompleteRequirements.length"
                                            class="inbox-reg-collapsed-hint"
                                        >
                                            {{ incompleteRequirements.length }} missing
                                        </span>
                                        <v-spacer></v-spacer>
                                        <button
                                            v-if="selected.communicationCount !== null && selected.communicationCount !== undefined"
                                            type="button"
                                            class="inbox-comm-count inbox-comm-count--link"
                                            title="View communications from this account in the last 30 days"
                                            @click.stop="openCommunicationHistory(selected)"
                                        >
                                            <v-icon x-small color="#2e7d32">mdi-history</v-icon>
                                            <span class="inbox-comm-count-num">{{ selected.communicationCount }}</span>
                                            <span>{{ selected.communicationCount === 1 ? "communication" : "communications" }}</span>
                                            <span class="inbox-comm-count-period">30d</span>
                                        </button>
                                    </div>

                                    <div v-show="!contextPanelCollapsed">
                                    <div class="inbox-reg-students">
                                        <div v-if="accountStudentsLoading" class="inbox-reg-loading">
                                            <v-progress-circular indeterminate size="14" width="2" color="success" class="mr-2"></v-progress-circular>
                                            Loading students...
                                        </div>
                                        <template v-else-if="accountStudents.length">
                                            <div
                                                v-for="student in accountStudents"
                                                :key="student.studentID"
                                                class="inbox-student-chip"
                                                :class="{
                                                    'inbox-student-chip--missing':
                                                        !student.studentRegistered,
                                                    'inbox-student-chip--skipcard':
                                                        studentHasSkipcard(student),
                                                }"
                                                :title="studentStatusTitle(student)"
                                                role="button"
                                                tabindex="0"
                                                @click.stop="openStudentDetails(student)"
                                                @keydown.enter.prevent="openStudentDetails(student)"
                                                @keydown.space.prevent="openStudentDetails(student)"
                                            >
                                                <v-icon
                                                    x-small
                                                    :color="studentStatusColor(student)"
                                                    class="mr-1"
                                                >
                                                    mdi-account
                                                </v-icon>
                                                {{ student.engName || student.hebName }}
                                                <span
                                                    v-if="isApplicantStudent(student)"
                                                    class="inbox-student-applicant"
                                                >
                                                    New Applicant Student
                                                </span>
                                                <span v-if="student.class" class="inbox-student-class">&middot; {{ student.class }}</span>
                                                <v-icon
                                                    v-if="studentHasSkipcard(student)"
                                                    x-small
                                                    color="#ef6c00"
                                                    class="inbox-student-skip-icon"
                                                    title="Has skip card"
                                                >
                                                    mdi-information
                                                </v-icon>
                                            </div>
                                        </template>
                                        <span v-else class="inbox-reg-value">No students on file</span>
                                    </div>

                                    <div v-if="incompleteRequirements.length" class="inbox-reg-missing">
                                        <div class="inbox-reg-label mb-1">Missing ({{ incompleteRequirements.length }})</div>
                                        <div class="inbox-reg-missing-list">
                                            <div
                                                v-for="(req, index) in incompleteRequirements"
                                                :key="index"
                                                class="inbox-reg-missing-item"
                                            >
                                                <div class="inbox-reg-missing-title">
                                                    <span
                                                        v-if="requirementStudentName(req)"
                                                        class="inbox-reg-missing-student"
                                                    >{{ requirementStudentName(req) }}:</span>
                                                    {{ req.reqType }}
                                                </div>
                                                <div
                                                    v-if="requirementExtraInfo(req)"
                                                    class="inbox-reg-missing-desc"
                                                >
                                                    {{ requirementExtraInfo(req) }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="inbox-reg-balance-row">
                                        <div class="inbox-reg-balance-item">
                                            <span class="inbox-reg-label">Tuition</span>
                                            <span
                                                class="inbox-reg-value"
                                                :class="{ 'inbox-reg-value--warning': selected.tuitionBalance > 0 }"
                                            >
                                                {{ (selected.tuitionBalance || 0) | currency }}
                                            </span>
                                        </div>
                                        <v-spacer></v-spacer>
                                        <v-btn
                                            small
                                            outline
                                            color="success"
                                            class="inbox-balance-btn"
                                            @click="openBalanceDialog"
                                        >
                                            <v-icon small>mdi-cash-multiple</v-icon>
                                            Balance details
                                        </v-btn>
                                    </div>
                                    </div>
                                </div>

                                <div
                                    v-show="!contextPanelCollapsed"
                                    class="inbox-action-bar"
                                >
                                    <div class="inbox-status-row">
                                        <span class="inbox-assign-label">Status:</span>
                                        <v-chip
                                            small
                                            text-color="white"
                                            :color="selected.status === 'COMPLETED' ? '#4caf50' : '#fdb400'"
                                        >
                                            <v-icon small color="white" class="mr-1">
                                                {{ selected.status === "COMPLETED" ? "mdi-check-circle" : "mdi-clock-outline" }}
                                            </v-icon>
                                            {{ selected.status === "COMPLETED" ? "Completed" : "Pending" }}
                                        </v-chip>
                                    </div>
                                    <div class="inbox-action-row">
                                        <span class="inbox-assign-label">Assigned to:</span>
                                        <v-chip
                                            small
                                            text-color="white"
                                            :color="assignedChipColor(selected.assignedTo)"
                                        >
                                            {{ assignedChipLabel(selected.assignedTo) }}
                                        </v-chip>
                                        <v-btn
                                            v-if="selected.assignedTo !== user"
                                            small
                                            color="success"
                                            :loading="processing"
                                            @click="assignToMe(selected)"
                                        >
                                            {{ isInboxUnassigned(selected.assignedTo) ? "Assign to me" : "Take over" }}
                                        </v-btn>
                                        <v-btn
                                            v-if="!isInboxUnassigned(selected.assignedTo)"
                                            small
                                            outline
                                            color="success"
                                            :loading="processing"
                                            @click="unassignUser(selected)"
                                        >
                                            Unassign
                                        </v-btn>
                                        <v-autocomplete
                                            v-model="assignToUser"
                                            class="inbox-assign-autocomplete"
                                            hide-details
                                            hide-no-data
                                            dense
                                            placeholder="Assign to user..."
                                            :items="users"
                                            :search-input.sync="loadUsers"
                                            :menu-props="userMenuProps"
                                            @focus="ensureUsersLoaded"
                                        ></v-autocomplete>
                                        <v-btn
                                            v-if="assignToUser"
                                            small
                                            color="success"
                                            :loading="processing"
                                            @click="assignToOther(selected)"
                                        >
                                            Assign
                                        </v-btn>
                                        <v-btn
                                            v-if="selected.status !== 'COMPLETED'"
                                            color="success"
                                            small
                                            :loading="bulkProcessing"
                                            @click="openCompleteDialog"
                                        >
                                            <v-icon small class="mr-1">mdi-check</v-icon>
                                            Mark done
                                        </v-btn>
                                        <v-btn
                                            v-if="!selected.needsAccount"
                                            color="success"
                                            small
                                            @click="openAccount(selected)"
                                        >
                                            <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                                            Go to account
                                        </v-btn>
                                    </div>
                                </div>
                            </div>

                            <div class="inbox-detail-scroll">
                                <v-layout row align-center class="mb-2 inbox-content-toolbar">
                                    <v-chip small text-color="white" :color="typeColor(selected.type)">
                                        <v-icon small color="white" class="mr-1">
                                            {{ typeIcon(selected.type) }}
                                        </v-icon>
                                        {{ typeLabel(selected.type) }}
                                    </v-chip>
                                    <v-chip
                                        v-if="selected.isNewStudent"
                                        small
                                        text-color="white"
                                        color="#1e88e5"
                                    >
                                        <v-icon small color="white" class="mr-1">mdi-account-plus</v-icon>
                                        New Student
                                    </v-chip>
                                    <v-btn
                                        v-if="selected.type === 'EMAIL' && selected.emailID"
                                        small
                                        outline
                                        color="success"
                                        class="ml-2"
                                        :loading="sendingReply"
                                        @click="openReply(false)"
                                    >
                                        <v-icon small class="mr-1">mdi-reply</v-icon>
                                        Reply
                                    </v-btn>
                                    <v-btn
                                        v-if="selected.type === 'EMAIL' && selected.emailID"
                                        small
                                        outline
                                        color="success"
                                        class="ml-2"
                                        :loading="sendingReply"
                                        @click="openReply(true)"
                                    >
                                        <v-icon small class="mr-1">mdi-reply-all</v-icon>
                                        Reply all
                                    </v-btn>
                                    <v-btn
                                        small
                                        outline
                                        color="success"
                                        class="ml-2"
                                        :loading="sendingForward"
                                        @click="openForward"
                                    >
                                        <v-icon small class="mr-1">mdi-share</v-icon>
                                        Forward
                                    </v-btn>
                                    <v-btn
                                        v-if="
                                            contextPanelCollapsed &&
                                                selected.status !== 'COMPLETED'
                                        "
                                        color="success"
                                        small
                                        class="ml-2"
                                        :loading="bulkProcessing"
                                        @click="openCompleteDialog"
                                    >
                                        <v-icon small class="mr-1">mdi-check</v-icon>
                                        Mark done
                                    </v-btn>
                                    <v-btn
                                        v-if="selected.type === 'SMS'"
                                        small
                                        outline
                                        color="success"
                                        class="ml-2"
                                        :disabled="selected.needsAccount"
                                        @click="openAccount(selected)"
                                    >
                                        <v-icon small class="mr-1">mdi-reply</v-icon>
                                        Reply
                                    </v-btn>
                                    <span
                                        v-if="selected.type === 'EMAIL' || selected.type === 'SMS'"
                                        class="inbox-from-inline ml-2"
                                    >
                                        <span class="inbox-from-inline-text">
                                            <span class="font-weight-medium">From:</span>
                                            {{ selected.type === "SMS" ? formatPhone(selected.from) : selected.from }}
                                        </span>
                                        <span
                                            v-if="selected.type === 'EMAIL' && selected.cc"
                                            class="inbox-from-inline-text inbox-cc-inline-text"
                                        >
                                            <span class="font-weight-medium">Cc:</span>
                                            {{ selected.cc }}
                                        </span>
                                        <span
                                            v-if="selected.type === 'EMAIL' && selected.bcc"
                                            class="inbox-from-inline-text inbox-cc-inline-text"
                                        >
                                            <span class="font-weight-medium">Bcc:</span>
                                            {{ selected.bcc }}
                                        </span>
                                        <v-menu
                                            offset-y
                                            :close-on-content-click="true"
                                            max-width="340"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-icon
                                                    small
                                                    color="#78909c"
                                                    class="inbox-email-meta-trigger"
                                                    v-on="on"
                                                >mdi-chevron-down</v-icon>
                                            </template>
                                            <v-card class="inbox-email-meta-card pa-3">
                                                <div class="inbox-email-meta-item">
                                                    <span class="inbox-email-meta-label">from:</span>
                                                    {{ selected.type === "SMS" ? formatPhone(selected.from) : selected.from }}
                                                </div>
                                                <div class="inbox-email-meta-item">
                                                    <span class="inbox-email-meta-label">to:</span>
                                                    {{ selected.type === "SMS" ? formatPhone(selected.to) : selected.to }}
                                                </div>
                                                <div v-if="selected.type === 'EMAIL' && selected.cc" class="inbox-email-meta-item">
                                                    <span class="inbox-email-meta-label">cc:</span>
                                                    {{ selected.cc }}
                                                </div>
                                                <div v-if="selected.type === 'EMAIL' && selected.bcc" class="inbox-email-meta-item">
                                                    <span class="inbox-email-meta-label">bcc:</span>
                                                    {{ selected.bcc }}
                                                </div>
                                                <div class="inbox-email-meta-item">
                                                    <span class="inbox-email-meta-label">date:</span>
                                                    {{ selected.date | time }}
                                                </div>
                                                <div v-if="selected.subject && selected.type !== 'SMS'" class="inbox-email-meta-item">
                                                    <span class="inbox-email-meta-label">subject:</span>
                                                    {{ selected.subject }}
                                                </div>
                                                <div v-if="mailedByDomain(selected.from)" class="inbox-email-meta-item">
                                                    <span class="inbox-email-meta-label">mailed-by:</span>
                                                    {{ mailedByDomain(selected.from) }}
                                                </div>
                                            </v-card>
                                        </v-menu>
                                    </span>
                                    <v-spacer></v-spacer>
                                    <v-btn
                                        small
                                        dark
                                        color="#1565c0"
                                        class="inbox-related-btn mr-3"
                                        :loading="relatedLoading"
                                        @click="openRelated"
                                    >
                                        <v-icon small color="white" class="mr-1">mdi-link-variant</v-icon>
                                        View related communications ({{ relatedCount || 0 }})
                                    </v-btn>
                                    <span class="caption grey--text">
                                        {{ selected.date | time }}
                                    </span>
                                </v-layout>

                                <template v-if="selected.type === 'EMAIL'">
                                    <div v-if="selected.subject" class="mb-3">
                                        <span class="font-weight-medium">Subject:</span>
                                        {{ selected.subject }}
                                    </div>
                                </template>
                                <template v-else-if="selected.type !== 'SMS' && selected.type !== 'CALLBACK REQUEST' && selected.type !== 'VOICEMAIL'">
                                    <div class="mb-1">
                                        <span class="font-weight-medium">From:</span>
                                        {{ formatPhone(selected.from) }}
                                    </div>
                                    <div class="mb-1">
                                        <span class="font-weight-medium">To:</span>
                                        {{ formatPhone(selected.to) }}
                                    </div>
                                    <div v-if="selected.subject" class="mb-3">
                                        <span class="font-weight-medium">Subject:</span>
                                        {{ selected.subject }}
                                    </div>
                                </template>

                                <div
                                    v-if="selected.type === 'SMS'"
                                    class="inbox-body"
                                >{{ trimText(selected.text) }}</div>
                                <div
                                    v-else-if="selected.type === 'CALLBACK REQUEST' || selected.type === 'VOICEMAIL'"
                                >
                                    <v-layout row wrap class="inbox-callback-layout">
                                        <v-flex xs12 sm5 class="inbox-callback-meta">
                                            <div class="mb-1">
                                                <span class="font-weight-medium">From:</span>
                                                {{ formatPhone(selected.from) }}
                                            </div>
                                            <div class="mb-1">
                                                <span class="font-weight-medium">To:</span>
                                                {{ formatPhone(selected.to) }}
                                            </div>
                                            <div v-if="selected.callerName" class="mb-1">
                                                <span class="font-weight-medium">Caller:</span>
                                                {{ selected.callerName }}
                                            </div>
                                            <div v-if="selected.subject" class="mb-3">
                                                <span class="font-weight-medium">Subject:</span>
                                                {{ selected.subject }}
                                            </div>
                                            <audio
                                                v-if="selected.recording"
                                                controls
                                                class="mb-2"
                                                :src="selected.recording"
                                            ></audio>
                                            <voicemail-preview
                                                v-else-if="selected.type === 'VOICEMAIL' && selected.voicemailID"
                                                :key="'inbox-voicemail-audio-' + selected.voicemailID"
                                                :row="voicemailPreviewRow(selected)"
                                                :hide-assign="true"
                                                :hide-meta="true"
                                                :hide-transcript="true"
                                            ></voicemail-preview>
                                            <div v-else class="grey--text mb-2">No recording</div>
                                            <div v-if="selected.note" class="mt-2">{{ selected.note }}</div>
                                        </v-flex>
                                        <v-flex xs12 sm7 class="inbox-callback-transcript">
                                            <div
                                                v-if="callbackSummary(selected) || callbackTranscription(selected)"
                                                class="inbox-transcript"
                                            >
                                                <div v-if="callbackSummary(selected)">
                                                    <div class="inbox-transcript-label">Summary</div>
                                                    <div
                                                        class="inbox-transcript-text"
                                                        dir="auto"
                                                        v-html="transcriptHtml(callbackSummary(selected))"
                                                    ></div>
                                                </div>
                                                <div
                                                    v-if="hasDistinctFullTranscript(selected)"
                                                    class="inbox-transcript-full"
                                                    :class="{ 'mt-2': !!callbackSummary(selected) }"
                                                >
                                                    <div
                                                        v-if="callbackSummary(selected)"
                                                        class="inbox-transcript-label inbox-transcript-label--toggle"
                                                        @click="toggleFullTranscription(selected)"
                                                    >
                                                        Full transcript
                                                        <v-icon small>
                                                            {{ selected.showFullTranscription ? "mdi-chevron-up" : "mdi-chevron-down" }}
                                                        </v-icon>
                                                    </div>
                                                    <div v-else class="inbox-transcript-label">
                                                        Transcript
                                                    </div>
                                                    <div
                                                        v-if="!callbackSummary(selected) || selected.showFullTranscription"
                                                        class="inbox-transcript-text"
                                                        dir="auto"
                                                        v-html="transcriptHtml(callbackTranscription(selected))"
                                                    ></div>
                                                </div>
                                            </div>
                                            <div
                                                v-else-if="selected.recording || selected.type === 'VOICEMAIL'"
                                                class="inbox-transcript-pending"
                                            >
                                                Transcription pending
                                            </div>
                                        </v-flex>
                                    </v-layout>
                                </div>
                                <div v-else-if="selected.type === 'EMAIL'">
                                    <email-body
                                        v-if="selected.emailID"
                                        :key="'inbox-email-' + selected.emailID + '-' + (selected.bodyHtml ? 'html' : 'pending')"
                                        :row="emailPreviewRow(selected)"
                                        :is-incoming="true"
                                    ></email-body>
                                    <div v-else-if="selected.text" class="inbox-body">{{ trimText(selected.text) }}</div>
                                </div>
                                <div v-else-if="selected.type === 'FAX'">
                                    <fax-preview
                                        v-if="selected.faxID"
                                        :key="'inbox-fax-' + selected.faxID"
                                        :row="faxPreviewRow(selected)"
                                        :hide-assign="true"
                                    ></fax-preview>
                                </div>

                                <v-divider
                                    v-if="
                                        (selected.type === 'EMAIL' && showReply) ||
                                            showForward
                                    "
                                    class="my-3"
                                ></v-divider>

                                <inbox-email-compose
                                    v-if="selected.type === 'EMAIL' && showReply"
                                    :key="'inbox-reply-' + selected.emailID + '-' + replyMode"
                                    :mode="replyMode"
                                    :reply-to="selected.from"
                                    :initial-to="replyMode === 'reply-all' ? selected.from : ''"
                                    :initial-cc="replyCc"
                                    :initial-bcc="replyBcc"
                                    :sending="sendingReply"
                                    @cancel="closeReply"
                                    @send="sendReply"
                                ></inbox-email-compose>

                                <inbox-email-compose
                                    v-if="showForward"
                                    mode="forward"
                                    :initial-subject="forwardSubject"
                                    :initial-body="forwardBody"
                                    :initial-attachments="forwardAttachments"
                                    :require-body="selected.type !== 'EMAIL'"
                                    :body-placeholder="
                                        selected.type === 'EMAIL'
                                            ? 'Optional note — the original email will be included'
                                            : 'Message'
                                    "
                                    :sending="sendingForward"
                                    @cancel="closeForward"
                                    @send="sendForward"
                                ></inbox-email-compose>
                            </div>
                        </v-card>
                    </v-flex>
                </v-layout>
            </div>
        </v-card>

        <v-dialog v-model="unlinkAccountDialog" max-width="420">
            <v-card v-if="selected">
                <v-card-title class="headline error white--text">
                    <v-icon left dark>mdi-link-variant-off</v-icon>
                    Unlink Account
                </v-card-title>
                <v-card-text class="pt-3 pb-2">
                    Remove the link between this communication and account
                    <strong>{{ selected.accountID }}</strong
                    >? It will move back to "Unknown account" until it's
                    linked again.
                </v-card-text>
                <v-card-actions class="pb-3">
                    <v-spacer></v-spacer>
                    <v-btn color="grey" dark @click="unlinkAccountDialog = false">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="error"
                        :loading="unlinkingAccount"
                        @click="confirmUnlinkAccount"
                    >
                        Unlink
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="balanceDialog" max-width="620">
            <v-card v-if="selected" class="pa-3">
                <v-card-title class="headline pl-0 pt-0">
                    Balance Details
                </v-card-title>
                <div class="inbox-balance-name mb-2">
                    {{ selected.name || selected.accountID }}
                    <span class="grey--text">&middot; {{ selected.accountID }}</span>
                </div>

                <v-switch
                    color="success"
                    class="mt-0 mb-1"
                    hide-details
                    v-model="showFurtherBilling"
                    @change="loadFurtherBilling"
                    label="Show Further Billing"
                ></v-switch>

                <v-layout row class="font-weight-medium">
                    <div style="width: 120px;">Date</div>
                    <div style="flex: 1 1 auto;">Description</div>
                    <div style="width: 90px;" class="text-xs-right">Amount</div>
                    <div style="width: 90px;" class="text-xs-right">Balance</div>
                </v-layout>
                <v-divider class="pb-1 mt-0"></v-divider>

                <div class="inbox-balance-transactions">
                    <div v-if="balanceDetailsLoading" class="text-xs-center pa-4">
                        <v-progress-circular indeterminate color="success" size="32"></v-progress-circular>
                    </div>
                    <template v-else>
                        <div v-for="(transaction, index) in balanceTransactions" :key="'t' + index">
                            <v-layout row>
                                <div style="width: 120px;">{{ transaction.date | date }}</div>
                                <div style="flex: 1 1 auto;">{{ transaction.description }}</div>
                                <div
                                    style="width: 90px;"
                                    class="text-xs-right font-weight-medium"
                                    v-if="transaction.txnType == 'D'"
                                >
                                    {{ transaction.amount | currency }}
                                </div>
                                <div
                                    style="width: 90px;"
                                    class="text-xs-right font-weight-medium"
                                    v-if="transaction.txnType == 'C'"
                                >
                                    ({{ transaction.amount | currency }})
                                </div>
                                <div style="width: 90px;" class="text-xs-right">
                                    {{ transaction.balance | currency }}
                                </div>
                            </v-layout>
                            <v-divider class="pb-1 mt-1"></v-divider>
                        </div>
                        <div v-for="(transaction, index) in futureBalanceTransactions" :key="'f' + index">
                            <v-layout row class="text-info">
                                <div style="width: 120px;">{{ transaction.date | date }}</div>
                                <div style="flex: 1 1 auto;">{{ transaction.description }}</div>
                                <div
                                    style="width: 90px;"
                                    class="text-xs-right font-weight-medium"
                                    v-if="transaction.type == 'transaction'"
                                >
                                    {{ transaction.amount | currency }}
                                </div>
                                <div
                                    style="width: 90px;"
                                    class="text-xs-right font-weight-medium"
                                    v-if="transaction.type == 'schedule'"
                                >
                                    ({{ transaction.amount | currency }})
                                </div>
                                <div style="width: 90px;" class="text-xs-right">
                                    {{ transaction.balance | currency }}
                                </div>
                            </v-layout>
                            <v-divider class="pb-1 mt-1"></v-divider>
                        </div>
                        <div
                            v-if="!balanceTransactions.length && !futureBalanceTransactions.length"
                            class="grey--text text-xs-center py-3"
                        >
                            No transaction history
                        </div>
                    </template>
                </div>

                <v-layout row class="mt-2 mr-1 title text-success" justify-space-between>
                    <div class="font-weight-medium">Total</div>
                    <div class="font-weight-medium">{{ balanceTransactionsTotal | currency }}</div>
                </v-layout>

                <v-card-actions class="mx-0 px-0 pb-0 mt-2">
                    <v-btn
                        color="success"
                        small
                        @click="balanceDialog = false; openAccount(selected)"
                    >
                        <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                        Go to account
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="default" dark @click="balanceDialog = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="bulkUpdateDialog" persistent max-width="640">
            <v-card class="inbox-complete-card">
                <div class="inbox-complete-header">
                    <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
                    <div class="inbox-complete-title">
                        Mark {{ selectedIds.length }}
                        {{ selectedIds.length === 1 ? "Communication" : "Communications" }}
                        Done
                    </div>
                </div>

                <div class="inbox-complete-body">
                    <div class="inbox-complete-list">
                        <div
                            v-for="item in selectedItemsList"
                            :key="item.communicationID"
                            class="inbox-complete-item"
                        >
                            <div
                                class="inbox-complete-item-icon"
                                :style="{ background: typeColor(item.type) }"
                            >
                                <v-icon small color="white">{{ typeIcon(item.type) }}</v-icon>
                            </div>
                            <div class="inbox-complete-item-body">
                                <div class="inbox-complete-item-header">
                                    <v-chip
                                        small
                                        text-color="white"
                                        :color="typeColor(item.type)"
                                        class="inbox-complete-item-chip"
                                    >
                                        {{ typeLabel(item.type) }}
                                    </v-chip>
                                    <span class="inbox-complete-item-name">
                                        {{ inboxItemName(item) }}
                                    </span>
                                    <span v-if="item.nameH" class="inbox-complete-item-name-he">
                                        {{ item.nameH }}
                                    </span>
                                    <span class="inbox-complete-item-date">
                                        {{ formatListDate(item.date) }} &middot; {{ formatListTime(item.date) }}
                                    </span>
                                </div>

                                <div
                                    v-if="!item.needsAccount"
                                    class="inbox-complete-item-account"
                                >
                                    <v-icon x-small color="#78909c" class="mr-1">mdi-account</v-icon>
                                    Account {{ item.accountID }}
                                    <span v-if="item.emailAddress"> &middot; {{ item.emailAddress }}</span>
                                    <span v-if="showFromContact(item)"> &middot; {{ formatPhone(item.from) }}</span>
                                </div>
                                <div v-else class="inbox-complete-item-account inbox-complete-item-account--missing">
                                    <v-icon x-small color="#fb8c00" class="mr-1">mdi-account-alert</v-icon>
                                    Unknown account
                                </div>

                                <fax-preview
                                    v-if="item.type === 'FAX' && item.faxID"
                                    :row="faxPreviewRow(item)"
                                    :hide-assign="true"
                                    :show-manage-files="false"
                                    class="inbox-complete-item-fax"
                                ></fax-preview>
                                <div
                                    v-else-if="item.type === 'CALLBACK REQUEST' || item.type === 'VOICEMAIL'"
                                    class="inbox-complete-item-message"
                                    dir="auto"
                                >{{ previewText(item) }}</div>
                                <div v-else class="inbox-complete-item-message">{{ trimText(item.text || item.subject) || "No message text" }}</div>
                            </div>
                        </div>
                    </div>

                    <isolated-textarea
                        v-if="bulkUpdateDialog"
                        ref="completeNote"
                        :value="bulkNote"
                        label="Note (required)"
                        hint="A note is required to mark a communication as done."
                        :persistent-hint="true"
                        :outline="true"
                        rows="3"
                        :autofocus="true"
                        textarea-class="mt-3 mb-0"
                        @has-text="completeNoteReady = $event"
                    ></isolated-textarea>
                </div>

                <v-card-actions class="inbox-complete-actions">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="default"
                        dark
                        :disabled="bulkProcessing"
                        @click="cancelBulkUpdateDialog"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="success"
                        :disabled="!completeNoteReady"
                        :loading="bulkProcessing"
                        @click="confirmBulkUpdate"
                    >
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="historyDialog" max-width="720">
            <v-card v-if="historyAccount" class="inbox-history-card">
                <div class="inbox-history-header">
                    <div>
                        <div class="inbox-history-title">
                            Communications
                            <span class="inbox-history-title-period">last 30 days</span>
                        </div>
                        <div class="inbox-history-account">
                            {{ historyAccount.name || historyAccount.accountID }}
                            <span>{{ historyAccount.accountID }}</span>
                        </div>
                    </div>
                    <v-btn icon small @click="historyDialog = false">
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="inbox-history-summary">
                    <span class="inbox-history-chip">{{ historyCounts.all }} total</span>
                    <span
                        v-if="historyPendingItems.length"
                        class="inbox-history-chip inbox-history-chip--open"
                    >
                        {{ historyPendingItems.length }} open
                    </span>
                    <span class="inbox-history-chip inbox-history-chip--sms">SMS {{ historyCounts.sms }}</span>
                    <span class="inbox-history-chip inbox-history-chip--callback">Callback {{ historyCounts.callback }}</span>
                    <span class="inbox-history-chip inbox-history-chip--email">Email {{ historyCounts.email }}</span>
                    <span class="inbox-history-chip inbox-history-chip--fax">Fax {{ historyCounts.fax }}</span>
                    <span class="inbox-history-chip inbox-history-chip--voicemail">Voicemail {{ historyCounts.voicemail }}</span>
                </div>
                <v-layout
                    v-if="!historyLoading && historyPendingItems.length"
                    row
                    align-center
                    class="inbox-history-toolbar"
                >
                    <div class="inbox-history-check" @click.stop>
                        <v-checkbox
                            :input-value="allHistoryPendingSelected"
                            :indeterminate="
                                historySelectedIds.length > 0 &&
                                    !allHistoryPendingSelected
                            "
                            color="success"
                            hide-details
                            @change="toggleHistorySelectAll"
                        ></v-checkbox>
                    </div>
                    <span class="inbox-history-toolbar-label">
                        Select open
                        <template v-if="historySelectedIds.length">
                            · {{ historySelectedIds.length }} selected
                        </template>
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="historySelectedIds.length"
                        small
                        color="success"
                        :loading="bulkProcessing"
                        @click="openHistoryCompleteDialog"
                    >
                        <v-icon small class="mr-1">mdi-check</v-icon>
                        Mark done
                    </v-btn>
                </v-layout>
                <div class="inbox-history-list">
                    <div v-if="historyLoading" class="text-xs-center pa-4">
                        <v-progress-circular indeterminate color="success" size="32"></v-progress-circular>
                    </div>
                    <div
                        v-else-if="!historyItems.length"
                        class="grey--text text-xs-center py-4"
                    >
                        No incoming communications in the last 30 days
                    </div>
                    <template v-else>
                        <div
                            v-for="item in historyItems"
                            :key="'h' + item.communicationID"
                            class="inbox-history-row"
                            :class="{ 'inbox-history-row--open': item.expanded }"
                        >
                            <div
                                class="inbox-history-row-header"
                                @click="toggleHistoryItem(item)"
                            >
                                <div
                                    v-if="isHistoryPending(item)"
                                    class="inbox-history-check"
                                    @click.stop
                                >
                                    <v-checkbox
                                        :input-value="isHistoryRowSelected(item)"
                                        color="success"
                                        hide-details
                                        @change="toggleHistoryRowSelection(item)"
                                    ></v-checkbox>
                                </div>
                                <div v-else class="inbox-history-check"></div>
                                <div
                                    class="inbox-history-typeicon"
                                    :style="{ background: typeColor(item.type) }"
                                >
                                    <v-icon x-small color="white">
                                        {{ typeIcon(item.type) }}
                                    </v-icon>
                                </div>
                                <div class="inbox-history-body">
                                    <div class="inbox-history-top">
                                        <strong>{{ typeLabel(item.type) }}</strong>
                                        <span
                                            v-if="item.hasAttachment"
                                            class="inbox-history-attach"
                                            :title="item.attachmentCount + ' attachment(s)'"
                                        >
                                            <v-icon x-small color="#607d8b">mdi-paperclip</v-icon>
                                            {{ item.attachmentCount }}
                                        </span>
                                        <span class="inbox-history-date">{{ formatListDate(item.date) }} {{ formatListTime(item.date) }}</span>
                                    </div>
                                    <div class="inbox-history-preview">
                                        {{ historyPreview(item) }}
                                    </div>
                                    <div class="inbox-history-meta">
                                        <span v-if="item.from">From {{ formatPhone(item.from) }}</span>
                                        <v-chip
                                            small
                                            text-color="white"
                                            :color="item.status === 'COMPLETED' ? '#4caf50' : '#fdb400'"
                                        >
                                            {{ item.status || "—" }}
                                        </v-chip>
                                        <v-chip
                                            small
                                            text-color="white"
                                            :color="assignedChipColor(item.assignedTo)"
                                        >
                                            {{ assignedChipLabel(item.assignedTo) }}
                                        </v-chip>
                                    </div>
                                    <div v-if="item.note" class="inbox-history-note">
                                        Note: {{ item.note }}
                                    </div>
                                </div>
                                <v-icon class="inbox-history-chevron">
                                    {{ item.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                                </v-icon>
                            </div>

                            <div v-if="item.expanded" class="inbox-history-expand">
                                <div
                                    v-if="item.type === 'SMS'"
                                    class="inbox-history-expand-text"
                                >{{ trimText(item.text) || "No message text" }}</div>
                                <div v-else-if="item.type === 'CALLBACK REQUEST' || item.type === 'VOICEMAIL'">
                                    <v-layout row wrap class="inbox-callback-layout">
                                        <v-flex xs12 sm5 class="inbox-callback-meta">
                                            <audio
                                                v-if="item.recording"
                                                controls
                                                class="mb-2"
                                                :src="item.recording"
                                            ></audio>
                                            <voicemail-preview
                                                v-else-if="item.type === 'VOICEMAIL' && item.voicemailID"
                                                :row="voicemailPreviewRow(item)"
                                                :hide-assign="true"
                                                :hide-meta="true"
                                                :hide-transcript="true"
                                            ></voicemail-preview>
                                            <div v-else class="grey--text mb-2">No recording</div>
                                        </v-flex>
                                        <v-flex xs12 sm7 class="inbox-callback-transcript">
                                            <div
                                                v-if="callbackSummary(item) || callbackTranscription(item)"
                                                class="inbox-transcript"
                                            >
                                                <div v-if="callbackSummary(item)">
                                                    <div class="inbox-transcript-label">Summary</div>
                                                    <div
                                                        class="inbox-transcript-text"
                                                        dir="auto"
                                                        v-html="transcriptHtml(callbackSummary(item))"
                                                    ></div>
                                                </div>
                                                <div
                                                    v-if="hasDistinctFullTranscript(item)"
                                                    class="inbox-transcript-full"
                                                    :class="{ 'mt-2': !!callbackSummary(item) }"
                                                >
                                                    <div
                                                        v-if="callbackSummary(item)"
                                                        class="inbox-transcript-label inbox-transcript-label--toggle"
                                                        @click.stop="toggleFullTranscription(item)"
                                                    >
                                                        Full transcript
                                                        <v-icon small>
                                                            {{ item.showFullTranscription ? "mdi-chevron-up" : "mdi-chevron-down" }}
                                                        </v-icon>
                                                    </div>
                                                    <div v-else class="inbox-transcript-label">
                                                        Transcript
                                                    </div>
                                                    <div
                                                        v-if="!callbackSummary(item) || item.showFullTranscription"
                                                        class="inbox-transcript-text"
                                                        dir="auto"
                                                        v-html="transcriptHtml(callbackTranscription(item))"
                                                    ></div>
                                                </div>
                                            </div>
                                            <div
                                                v-else-if="item.recording || item.type === 'VOICEMAIL'"
                                                class="inbox-transcript-pending"
                                            >
                                                Transcription pending
                                            </div>
                                        </v-flex>
                                    </v-layout>
                                </div>
                                <email-body
                                    v-else-if="item.type === 'EMAIL' && item.emailID"
                                    :row="emailPreviewRow(item)"
                                ></email-body>
                                <fax-preview
                                    v-else-if="item.type === 'FAX' && item.faxID"
                                    :row="faxPreviewRow(item)"
                                    :hide-assign="true"
                                ></fax-preview>
                            </div>
                        </div>
                    </template>
                </div>
                <div v-if="historyCompleteOpen" class="inbox-history-complete">
                    <div class="inbox-history-complete-title">
                        Mark {{ historySelectedIds.length }}
                        {{
                            historySelectedIds.length === 1
                                ? "communication"
                                : "communications"
                        }}
                        done
                    </div>
                    <isolated-textarea
                        v-if="historyCompleteOpen"
                        ref="historyNote"
                        :value="bulkNote"
                        label="Note (required)"
                        hint="This note will be applied to every selected communication."
                        :persistent-hint="true"
                        :outline="true"
                        rows="3"
                        :autofocus="true"
                        textarea-class="mt-2 mb-0"
                        @has-text="historyNoteReady = $event"
                    ></isolated-textarea>
                    <div class="inbox-history-complete-actions">
                        <v-btn
                            color="default"
                            dark
                            :disabled="bulkProcessing"
                            @click="cancelHistoryComplete"
                        >
                            Cancel
                        </v-btn>
                        <v-btn
                            color="success"
                            :disabled="!historyNoteReady"
                            :loading="bulkProcessing"
                            @click="confirmHistoryComplete"
                        >
                            Confirm
                        </v-btn>
                    </div>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="relatedDialog" max-width="720">
            <v-card class="inbox-history-card">
                <div class="inbox-history-header">
                    <div>
                        <div class="inbox-history-title">
                            Sent from this communication
                        </div>
                        <div v-if="selected && selected.subject" class="inbox-history-account">
                            {{ selected.subject }}
                        </div>
                    </div>
                    <v-btn icon small @click="relatedDialog = false">
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="inbox-history-list">
                    <div v-if="relatedLoading" class="text-xs-center pa-4">
                        <v-progress-circular indeterminate color="success" size="32"></v-progress-circular>
                    </div>
                    <div
                        v-else-if="!relatedItems.length"
                        class="grey--text text-xs-center py-4"
                    >
                        No replies or forwards have been sent from this communication
                    </div>
                    <template v-else>
                        <div
                            v-for="item in relatedItems"
                            :key="'related-' + item.id"
                            class="inbox-history-row"
                            :class="{ 'inbox-history-row--open': item.expanded }"
                        >
                            <div
                                class="inbox-history-row-header"
                                @click="toggleRelatedItem(item)"
                            >
                                <v-chip
                                    small
                                    text-color="white"
                                    :color="relatedKindColor(item)"
                                    class="mr-2"
                                >
                                    {{ relatedKindLabel(item) }}
                                </v-chip>
                                <div class="inbox-history-row-main">
                                    <div class="inbox-history-row-title">
                                        {{ item.to || item.subject || "Outgoing email" }}
                                        <v-icon
                                            v-if="item.hasAttachments"
                                            small
                                            color="#607d8b"
                                            class="ml-1"
                                        >
                                            mdi-paperclip
                                        </v-icon>
                                    </div>
                                    <div class="inbox-history-row-preview">
                                        <span v-if="item.cc">Cc: {{ item.cc }}</span>
                                        <span v-if="item.bcc">
                                            {{ item.cc ? " · " : "" }}Bcc: {{ item.bcc }}
                                        </span>
                                        <span v-if="!item.cc && !item.bcc">
                                            {{ item.subject }}
                                        </span>
                                    </div>
                                </div>
                                <div class="inbox-history-row-meta">
                                    {{ item.date | time }}
                                    <div v-if="item.userName" class="caption grey--text">
                                        {{ item.userName }}
                                    </div>
                                </div>
                                <v-icon>
                                    {{ item.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                                </v-icon>
                            </div>
                            <div v-if="item.expanded" class="inbox-history-row-body">
                                <div class="mb-2">
                                    <div><span class="font-weight-medium">From:</span> {{ item.from }}</div>
                                    <div><span class="font-weight-medium">To:</span> {{ item.to }}</div>
                                    <div v-if="item.cc"><span class="font-weight-medium">Cc:</span> {{ item.cc }}</div>
                                    <div v-if="item.bcc"><span class="font-weight-medium">Bcc:</span> {{ item.bcc }}</div>
                                    <div v-if="item.subject"><span class="font-weight-medium">Subject:</span> {{ item.subject }}</div>
                                </div>
                                <email-body
                                    :row="relatedPreviewRow(item)"
                                    :is-incoming="false"
                                    :show-manage-files="false"
                                ></email-body>
                            </div>
                        </div>
                    </template>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="studentDialog" max-width="680">
            <v-card v-if="selectedStudent" class="inbox-student-card">
                <div class="inbox-student-header">
                    <div>
                        <div class="inbox-student-title">Student details</div>
                        <div class="inbox-student-subtitle">
                            {{ studentDisplayName(selectedStudent) }}
                            <span v-if="selectedStudent.class">
                                &middot; {{ selectedStudent.class }}
                            </span>
                        </div>
                    </div>
                    <v-btn icon small @click="studentDialog = false">
                        <v-icon small color="white">mdi-close</v-icon>
                    </v-btn>
                </div>

                <div class="inbox-student-body">
                    <div class="inbox-student-status-row">
                        <v-chip
                            small
                            text-color="white"
                            :color="studentStatusColor(selectedStudent)"
                        >
                            {{ studentStatusTitle(selectedStudent, true) }}
                        </v-chip>
                        <v-chip
                            v-if="isApplicantStudent(selectedStudent)"
                            small
                            text-color="white"
                            color="#ef6c00"
                        >
                            New Applicant Student
                        </v-chip>
                        <v-chip
                            v-if="selectedStudent.nonUTA"
                            small
                            text-color="white"
                            color="#1565c0"
                        >
                            Non UTA
                        </v-chip>
                        <div
                            v-if="studentHasSkipcard(selectedStudent)"
                            class="inbox-student-skipcard"
                        >
                            <v-icon x-small color="#ef6c00" class="mr-1">mdi-alert-circle</v-icon>
                            <span class="inbox-student-skipcard-label">Skip card</span>
                            {{ selectedStudent.skipcard }}
                        </div>
                    </div>

                    <div class="inbox-student-details">
                        <div
                            v-for="row in studentDetailRows(selectedStudent)"
                            :key="row.label"
                            class="inbox-student-detail-row"
                        >
                            <span class="inbox-student-detail-label">{{ row.label }}</span>
                            <span class="inbox-student-detail-value">{{ row.value }}</span>
                        </div>
                    </div>

                    <div
                        v-if="studentMissingRequirements(selectedStudent).length"
                        class="inbox-student-missing"
                    >
                        <div class="inbox-reg-label mb-1">
                            Missing
                            ({{ studentMissingRequirements(selectedStudent).length }})
                        </div>
                        <div class="inbox-student-missing-list">
                            <div
                                v-for="(req, index) in studentMissingRequirements(selectedStudent)"
                                :key="'sm' + index"
                                class="inbox-reg-missing-item"
                            >
                                <div class="inbox-reg-missing-title">{{ req.reqType }}</div>
                                <div
                                    v-if="requirementExtraInfo(req)"
                                    class="inbox-reg-missing-desc"
                                >
                                    {{ requirementExtraInfo(req) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <v-card-actions class="inbox-student-actions">
                    <v-btn
                        v-if="selected && selected.accountID && !selected.needsAccount"
                        color="success"
                        outline
                        small
                        @click="openAccount(selected)"
                    >
                        <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                        Go to account
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="default" dark small @click="studentDialog = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="manageFiles.open"
            persistent
            content-class="inbox-manage-files-dialog"
        >
            <v-card class="inbox-manage-files-card">
                <document-splitter
                    v-if="manageFiles.open && manageFiles.source && manageFiles.sourceId"
                    :key="'manage-files-' + manageFiles.source + '-' + manageFiles.sourceId"
                    :embedded="true"
                    :file-source="manageFiles.source"
                    :file-source-id="manageFiles.sourceId"
                    @saved="onManageFilesSaved"
                >
                    <template slot="header-actions">
                        <v-btn
                            v-if="canMarkManageFilesDone"
                            outline
                            small
                            color="white"
                            class="files-back ma-0 ml-2"
                            :loading="bulkProcessing"
                            @click="openCompleteFromManageFiles"
                        >
                            <v-icon small class="mr-1">mdi-check</v-icon>
                            Mark done
                        </v-btn>
                        <v-btn
                            color="default"
                            dark
                            small
                            class="ma-0 ml-2"
                            @click="closeManageFiles"
                        >
                            Close
                        </v-btn>
                    </template>
                </document-splitter>
            </v-card>
        </v-dialog>

        <v-dialog v-model="manageFilesClosePrompt" persistent max-width="520">
            <v-card class="inbox-complete-card">
                <div class="inbox-complete-header">
                    <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
                    <div class="inbox-complete-title">Mark communication done?</div>
                </div>
                <div class="inbox-complete-body">
                    <div class="inbox-manage-files-close-copy">
                        A document was imported. You can mark this communication
                        as done now, or close without changing its status.
                    </div>
                    <isolated-textarea
                        v-if="manageFilesClosePrompt"
                        ref="manageFilesNote"
                        :value="manageFilesCloseNote"
                        label="Note (required to mark done)"
                        hint="A note is required to mark a communication as done."
                        :persistent-hint="true"
                        :outline="true"
                        rows="3"
                        :autofocus="true"
                        textarea-class="mt-3 mb-0"
                        @has-text="manageFilesNoteReady = $event"
                    ></isolated-textarea>
                </div>
                <v-card-actions class="inbox-complete-actions">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="default"
                        dark
                        :disabled="bulkProcessing"
                        @click="dismissManageFiles"
                    >
                        Close
                    </v-btn>
                    <v-btn
                        v-if="canMarkManageFilesDone"
                        color="success"
                        :disabled="!manageFilesNoteReady"
                        :loading="bulkProcessing"
                        @click="closeAndMarkManageFilesDone"
                    >
                        Close &amp; mark done
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import * as firebase from "firebase/app";
import "firebase/database";
import moment from "moment";
import { formatPhoneNumber } from "../../utils/helpers.js";
import DocumentSplitter from "../DocumentSplitter/DocumentSplitter.vue";
import IsolatedTextarea from "../components/IsolatedTextarea.vue";
import InboxEmailCompose from "../components/InboxEmailCompose.vue";

export default {
    components: {
        DocumentSplitter: DocumentSplitter,
        IsolatedTextarea: IsolatedTextarea,
        InboxEmailCompose: InboxEmailCompose,
    },
    provide() {
        return {
            openManageFilesDialog: (payload) => this.openManageFilesDialog(payload),
        };
    },
    data() {
        return {
            loading: false,
            processing: false,
            items: [],
            counts: {
                all: 0,
                sms: 0,
                callback: 0,
                email: 0,
                fax: 0,
                voicemail: 0,
                needsAccount: 0,
                assignedToMe: 0,
                unassigned: 0,
            },
            filters: {
                search: "",
                assignedFilter: "mineAndUnassigned",
                assignedToUser: "",
                status: "PENDING",
                types: [],
                fromDate: "",
                toDate: "",
            },
            onlyNeedsAccount: false,
            sortBy: "newest",
            fromDateMenu: false,
            toDateMenu: false,
            typeOptions: [
                { label: "SMS", value: "SMS", countKey: "sms" },
                { label: "Callback", value: "CALLBACK REQUEST", countKey: "callback" },
                { label: "Email", value: "EMAIL", countKey: "email" },
                { label: "Fax", value: "FAX", countKey: "fax" },
                { label: "Voicemail", value: "VOICEMAIL", countKey: "voicemail" },
            ],
            assignedFilterOptions: [
                { text: "My Queue (unassigned + mine)", value: "mineAndUnassigned" },
                { text: "Unassigned only", value: "unassigned" },
                { text: "Assigned to me", value: "mine" },
                { text: "Specific user...", value: "user" },
                { text: "Everyone", value: "all" },
            ],
            statusOptions: [
                { text: "Pending", value: "PENDING" },
                { text: "Completed", value: "COMPLETED" },
                { text: "All", value: "ALL" },
            ],
            sortOptions: [
                { text: "Newest first", value: "newest" },
                { text: "Oldest first", value: "oldest" },
                { text: "Type", value: "type" },
                { text: "Account name", value: "name" },
            ],
            selected: null,
            showReply: false,
            replyMode: "reply",
            replyBody: "",
            replyCc: "",
            replyBcc: "",
            sendingReply: false,
            showForward: false,
            sendingForward: false,
            forwardTo: "",
            forwardCc: "",
            forwardBcc: "",
            forwardSubject: "",
            forwardBody: "",
            forwardAttachments: [],
            forwardAttachmentsLoaded: false,
            completeNoteReady: false,
            historyNoteReady: false,
            manageFilesNoteReady: false,
            contextPanelCollapsed: false,
            assignAccountID: "",
            assignAccountInfo: null,
            unlinkAccountDialog: false,
            unlinkingAccount: false,
            errorMessage: "",
            user: "",
            users: [],
            loadUsers: null,
            assignToUser: "",
            selectedIds: [],
            balanceDialog: false,
            balanceDetailsLoading: false,
            balanceTransactions: [],
            futureBalanceTransactions: [],
            balanceTransactionsTotal: 0,
            showFurtherBilling: false,
            accountStudents: [],
            accountStudentsLoading: false,
            accountInfoCache: {},
            studentDialog: false,
            selectedStudent: null,
            registrationRequirements: [],
            registrationLoading: false,
            bulkUpdateDialog: false,
            bulkNote: "",
            bulkProcessing: false,
            completingSingle: false,
            completingFromHistory: false,
            manageFiles: {
                open: false,
                source: "",
                sourceId: "",
                communicationID: null,
                didSave: false,
            },
            manageFilesClosePrompt: false,
            manageFilesCloseNote: "",
            historyDialog: false,
            historyLoading: false,
            historyAccount: null,
            historyItems: [],
            historySelectedIds: [],
            historyCompleteOpen: false,
            relatedDialog: false,
            relatedLoading: false,
            relatedItems: [],
            relatedCount: 0,
            historyCounts: {
                all: 0,
                sms: 0,
                callback: 0,
                email: 0,
                fax: 0,
                voicemail: 0,
            },
            firebaseNavRef: null,
            firebaseInboxRef: null,
            firebaseNavReady: false,
            firebaseInboxReady: false,
            lastInboxLoadAt: 0,
            inboxLoadId: 0,
            pendingInboxReloadTimeout: null,
            inboxHasMore: false,
            loadingMore: false,
            inboxPageSize: 50,
            inboxSkip: 0,
            inboxTotal: 0,
            inboxVisibilityHandler: null,
            userMenuProps: {
                offsetY: true,
                maxHeight: 280,
                contentClass: "inbox-user-menu",
            },
        };
    },
    computed: {
        isAdmin() {
            const isAdmin = this.$store.getters["authentication/isAdmin"];

            if (!isAdmin || isAdmin == "false") return false;

            return true;
        },
        visibleAssignedFilterOptions() {
            if (this.isAdmin) {
                return this.assignedFilterOptions;
            }

            return this.assignedFilterOptions.filter(
                (option) => option.value !== "user" && option.value !== "all",
            );
        },
        incompleteRequirements() {
            return this.registrationRequirements.filter(
                (req) => !req.isCompleted,
            );
        },
        selectedItemsList() {
            var ids = this.selectedIds;
            var list = this.items.filter(function(item) {
                return ids.indexOf(item.communicationID) !== -1;
            });
            var self = this;
            ids.forEach(function(id) {
                if (list.some(function(item) {
                    return item.communicationID === id;
                })) {
                    return;
                }
                if (self.selected && self.selected.communicationID === id) {
                    list.push(self.selected);
                    return;
                }
                var historyItem = self.historyItems.find(function(item) {
                    return item.communicationID === id;
                });
                if (historyItem) {
                    list.push(historyItem);
                }
            });
            return list;
        },
        canMarkManageFilesDone() {
            var item = this.manageFilesItem;
            return !!(item && item.status !== "COMPLETED");
        },
        manageFilesItem() {
            var id = this.manageFiles.communicationID;
            if (!id && this.selected) {
                if (
                    this.manageFiles.source === "email" &&
                    this.selected.emailID &&
                    String(this.selected.emailID) === String(this.manageFiles.sourceId)
                ) {
                    return this.selected;
                }
                if (
                    this.manageFiles.source === "fax" &&
                    this.selected.faxID &&
                    String(this.selected.faxID) === String(this.manageFiles.sourceId)
                ) {
                    return this.selected;
                }
            }
            if (!id) {
                return null;
            }
            var fromItems = this.items.find(function(item) {
                return item.communicationID === id;
            });
            if (fromItems) {
                return fromItems;
            }
            if (this.selected && this.selected.communicationID === id) {
                return this.selected;
            }
            return this.historyItems.find(function(item) {
                return item.communicationID === id;
            }) || null;
        },
        allFilteredSelected() {
            return (
                this.filteredItems.length > 0 &&
                this.filteredItems.every(
                    (item) => this.selectedIds.indexOf(item.communicationID) !== -1,
                )
            );
        },
        historyPendingItems() {
            return this.historyItems.filter(function(item) {
                return item && item.status !== "COMPLETED";
            });
        },
        allHistoryPendingSelected() {
            return (
                this.historyPendingItems.length > 0 &&
                this.historyPendingItems.every(
                    (item) =>
                        this.historySelectedIds.indexOf(item.communicationID) !== -1,
                )
            );
        },
        canSendForward() {
            if (!this.forwardTo || !this.forwardTo.trim()) {
                return false;
            }
            if (
                this.selected &&
                this.selected.type !== "EMAIL" &&
                (!this.forwardBody || !this.forwardBody.trim())
            ) {
                return false;
            }
            return true;
        },
        usesDefaultDateRange() {
            var status = this.filters.status;
            return (
                (status === "COMPLETED" || status === "ALL") &&
                !(this.filters.fromDate || "").trim()
            );
        },
        inboxRangeLabel() {
            var loaded = this.filteredItems.length;
            var total = this.inboxRangeTotal;
            if (!total && !loaded) {
                return "0 of 0";
            }

            if (!loaded) {
                return "0 of " + this.formatInboxCount(total);
            }

            var end = loaded;
            if (total && end > total) {
                end = total;
            }

            return (
                "1–" +
                this.formatInboxCount(end) +
                " of " +
                this.formatInboxCount(total || end)
            );
        },
        inboxRangeTotal() {
            var types = this.filters.types || [];
            if (types.length) {
                var keys = {
                    SMS: "sms",
                    "CALLBACK REQUEST": "callback",
                    EMAIL: "email",
                    FAX: "fax",
                    VOICEMAIL: "voicemail",
                };
                return types.reduce((sum, type) => {
                    return sum + (this.counts[keys[type]] || 0);
                }, 0);
            }

            if (this.inboxTotal) {
                return this.inboxTotal;
            }

            return this.counts.all || 0;
        },
        liveCounts() {
            var status = this.filters.status;
            var user = this.user;
            var self = this;

            var list = this.items.filter(function(item) {
                if (!self.canSeeInboxAssignment(item.assignedTo)) {
                    return false;
                }
                if (status && status !== "ALL" && item.status !== status) {
                    return false;
                }
                return true;
            });

            return {
                all: list.length,
                sms: list.filter(function(item) {
                    return item.type === "SMS";
                }).length,
                callback: list.filter(function(item) {
                    return item.type === "CALLBACK REQUEST";
                }).length,
                email: list.filter(function(item) {
                    return item.type === "EMAIL";
                }).length,
                fax: list.filter(function(item) {
                    return item.type === "FAX";
                }).length,
                voicemail: list.filter(function(item) {
                    return item.type === "VOICEMAIL";
                }).length,
                needsAccount: list.filter(function(item) {
                    return item.needsAccount;
                }).length,
                assignedToMe: list.filter(function(item) {
                    return !self.isInboxUnassigned(item.assignedTo) && item.assignedTo === user;
                }).length,
                unassigned: list.filter(function(item) {
                    return self.isInboxUnassigned(item.assignedTo);
                }).length,
            };
        },
        filteredItems() {
            var search = (this.filters.search || "").toLowerCase().trim();
            var selectedTypes = this.filters.types;

            var list = this.items.filter((item) => {
                if (!this.canSeeInboxAssignment(item.assignedTo)) {
                    return false;
                }

                if (selectedTypes.length && selectedTypes.indexOf(item.type) === -1) {
                    return false;
                }

                if (
                    this.filters.status &&
                    this.filters.status !== "ALL" &&
                    item.status !== this.filters.status
                ) {
                    return false;
                }

                if (this.onlyNeedsAccount && !item.needsAccount) {
                    return false;
                }

                if (!search) {
                    return true;
                }

                var haystack = [
                    item.name,
                    item.nameH,
                    item.accountID,
                    item.from,
                    item.to,
                    item.subject,
                    item.text,
                    item.assignedTo,
                    item.callerName,
                    item.transcription,
                    item.transcriptionSummary,
                ]
                    .join(" ")
                    .toLowerCase();

                return haystack.indexOf(search) !== -1;
            });

            return this.sortItems(list);
        },
    },
    watch: {
        async loadUsers(value) {
            if (!value || !value.trim()) {
                await this.loadAllUsers();
                return;
            }

            const response = await this.$http.get(
                `/odata/users?$count=true&$filter=(contains(userName, '${value.trim()}'))`,
            );

            this.users = response.value.map((u) => u.userName);
        },
        historyDialog(open) {
            if (!open) {
                this.historySelectedIds = [];
                this.historyCompleteOpen = false;
                if (this.completingFromHistory) {
                    this.selectedIds = [];
                    this.completingFromHistory = false;
                }
            }
        },
    },
    created() {
        this.user = this.$store.state.authentication.user;
        this.loadPersistedFilters();
        this.loadAllUsers();

        this.clampAssignedFilterForRole();

        this.loadInbox();
        this.firebaseSetup();
        this.bindInboxVisibility();
    },
    mounted() {
        this.setInboxFillHeight(true);
    },
    beforeDestroy() {
        this.setInboxFillHeight(false);
        this.unbindInboxVisibility();
        if (this.pendingInboxReloadTimeout) {
            clearTimeout(this.pendingInboxReloadTimeout);
            this.pendingInboxReloadTimeout = null;
        }
        if (this.firebaseNavRef) {
            this.firebaseNavRef.off();
        }
        if (this.firebaseInboxRef) {
            this.firebaseInboxRef.off();
        }
    },
    methods: {
        async loadAllUsers() {
            try {
                const response = await this.$http.get(
                    "/odata/users?$count=true&$orderby=userName asc",
                );
                this.users = (response && response.value ? response.value : []).map(
                    function(u) {
                        return u.userName;
                    },
                );
            } catch (e) {
                this.users = [];
            }
        },
        ensureUsersLoaded() {
            if (!this.users.length) {
                this.loadAllUsers();
            }
        },
        setInboxFillHeight(enabled) {
            var coreView = document.getElementById("core-view");
            if (coreView) {
                if (enabled) {
                    coreView.classList.add("core-view--inbox");
                } else {
                    coreView.classList.remove("core-view--inbox");
                }
            }
            if (document.body) {
                if (enabled) {
                    document.body.classList.add("inbox-fill-height");
                } else {
                    document.body.classList.remove("inbox-fill-height");
                }
            }
        },
        async loadInbox(options) {
            var silent = false;
            var quietErrors = false;
            var append = false;
            if (options === true) {
                silent = true;
            } else if (options && typeof options === "object") {
                silent = !!options.silent;
                quietErrors = !!options.quietErrors;
                append = !!options.append;
            }

            if (append) {
                if (this.loading || this.loadingMore || !this.inboxHasMore) {
                    return;
                }
            } else if (this.pendingInboxReloadTimeout) {
                clearTimeout(this.pendingInboxReloadTimeout);
                this.pendingInboxReloadTimeout = null;
            }

            if (!append) {
                this.lastInboxLoadAt = Date.now();
                this.inboxLoadId += 1;
                this.inboxHasMore = true;
                this.inboxSkip = 0;
            }

            var loadId = this.inboxLoadId;
            if (append) {
                this.loadingMore = true;
            } else if (silent !== true) {
                this.loading = true;
            }
            this.errorMessage = "";
            this.persistFilters();

            var assignedFilter = this.effectiveAssignedFilter();
            var skip = append ? this.inboxSkip : 0;
            var keepLoaded =
                !append &&
                silent &&
                this.items.length > this.inboxPageSize;
            var take = append
                ? this.inboxPageSize
                : keepLoaded
                    ? Math.min(500, this.items.length)
                    : this.inboxPageSize;
            var typeParam = (this.filters.types || []).join(",");

            var query =
                "api/communications/getInbox?type=" +
                encodeURIComponent(typeParam) +
                "&status=" +
                encodeURIComponent(this.filters.status) +
                "&user=" +
                encodeURIComponent(this.user) +
                "&assignedFilter=" +
                encodeURIComponent(assignedFilter) +
                "&assignedToUser=" +
                encodeURIComponent(
                    assignedFilter === "user" ? this.filters.assignedToUser || "" : "",
                ) +
                "&fromDate=" +
                encodeURIComponent(this.filters.fromDate || "") +
                "&toDate=" +
                encodeURIComponent(this.filters.toDate || "") +
                "&skip=" +
                skip +
                "&take=" +
                take +
                "&sortBy=" +
                encodeURIComponent(this.sortBy === "oldest" ? "oldest" : "newest");

            try {
                var httpConfig = quietErrors ? { notifyError: false } : undefined;
                const response = await this.$http.get(query, httpConfig);

                if (loadId !== this.inboxLoadId) {
                    return;
                }

                var incoming = this.restrictInboxItems((response && response.items) || []);
                this.inboxSkip = skip + ((response && response.items) || []).length;
                if (append) {
                    var seen = {};
                    this.items.forEach(function(item) {
                        seen[item.communicationID] = true;
                    });
                    this.items = this.items.concat(
                        incoming.filter(function(item) {
                            return !seen[item.communicationID];
                        }),
                    );
                } else {
                    this.items = incoming;
                    if (response && response.counts) {
                        this.counts = response.counts;
                    }
                }

                if (response && response.total != null) {
                    this.inboxTotal = response.total;
                } else if (
                    response &&
                    response.counts &&
                    response.counts.all != null &&
                    !(this.filters.types || []).length
                ) {
                    this.inboxTotal = response.counts.all;
                }

                this.inboxHasMore = !!(response && response.hasMore);

                if (this.selected) {
                    this.selected = this.adoptInboxSelection(
                        this.selected,
                        this.items,
                    );
                }
            } catch (e) {
                if (loadId !== this.inboxLoadId) {
                    return;
                }
                if (append) {
                    this.inboxHasMore = false;
                }
                if (!quietErrors) {
                    this.errorMessage = "Could not load communications";
                }
            }

            if (loadId === this.inboxLoadId) {
                this.loading = false;
                this.loadingMore = false;
                var self = this;
                this.$nextTick(function() {
                    self.onInboxListScroll();
                });
            }
        },
        onInboxListScroll() {
            var list = this.$refs.inboxList;
            if (!list || this.loading || this.loadingMore || !this.inboxHasMore) {
                return;
            }

            if (list.scrollTop + list.clientHeight >= list.scrollHeight - 160) {
                this.loadInbox({ append: true, silent: true, quietErrors: true });
            }
        },
        formatInboxCount(value) {
            var n = Number(value) || 0;
            return n.toLocaleString();
        },
        sortItems(list) {
            var sorted = list.slice();

            if (this.sortBy === "oldest") {
                sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
            } else if (this.sortBy === "type") {
                sorted.sort(
                    (a, b) =>
                        (a.type || "").localeCompare(b.type || "") ||
                        new Date(b.date) - new Date(a.date),
                );
            } else if (this.sortBy === "name") {
                sorted.sort((a, b) =>
                    (a.name || a.accountID || "").localeCompare(
                        b.name || b.accountID || "",
                    ),
                );
            } else {
                sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
            }

            return sorted;
        },
        setAssignedFilter(value) {
            this.filters.assignedFilter = value;
            this.loadInbox();
        },
        toggleType(value) {
            var index = this.filters.types.indexOf(value);
            if (index === -1) {
                this.filters.types.push(value);
            } else {
                this.filters.types.splice(index, 1);
            }
            this.persistFilters();
            this.loadInbox();
        },
        persistFilters() {
            try {
                localStorage.setItem(
                    "communicationsInboxFilters",
                    JSON.stringify({
                        assignedFilter: this.filters.assignedFilter,
                        status: this.filters.status,
                        types: this.filters.types,
                        sortBy: this.sortBy,
                        contextCollapsed: this.contextPanelCollapsed,
                    }),
                );
            } catch (e) {
                // ignore storage errors
            }
        },
        loadPersistedFilters() {
            try {
                var saved = localStorage.getItem("communicationsInboxFilters");
                if (!saved) return;

                var parsed = JSON.parse(saved);
                if (parsed.assignedFilter) {
                    this.filters.assignedFilter = parsed.assignedFilter;
                }
                if (parsed.status) {
                    this.filters.status = parsed.status;
                }
                if (Array.isArray(parsed.types)) {
                    this.filters.types = parsed.types;
                }
                if (parsed.sortBy) {
                    this.sortBy = parsed.sortBy;
                }
                if (typeof parsed.contextCollapsed === "boolean") {
                    this.contextPanelCollapsed = parsed.contextCollapsed;
                }
            } catch (e) {
                // ignore storage errors
            }
        },
        openCommunicationHistory(item) {
            if (!item || !item.accountID || item.needsAccount) {
                return;
            }

            this.historyAccount = item;
            this.historyDialog = true;
            this.loadCommunicationHistory(item.accountID);
        },
        async loadCommunicationHistory(accountID) {
            this.historyLoading = true;
            this.historyItems = [];
            this.historySelectedIds = [];
            this.historyCompleteOpen = false;
            this.historyCounts = {
                all: 0,
                sms: 0,
                callback: 0,
                email: 0,
                fax: 0,
                voicemail: 0,
            };

            try {
                var response = await this.$http.get(
                    "api/communications/getRecentForAccount?accountID=" +
                        encodeURIComponent(accountID),
                );
                if (
                    !this.historyAccount ||
                    this.historyAccount.accountID !== accountID
                ) {
                    return;
                }

                this.historyItems = this.restrictInboxItems(
                    ((response && response.items) || []).map(function(item) {
                        item.expanded = false;
                        return item;
                    }),
                );
                this.historyCounts = {
                    all: this.historyItems.length,
                    sms: this.historyItems.filter(function(item) {
                        return item.type === "SMS";
                    }).length,
                    callback: this.historyItems.filter(function(item) {
                        return item.type === "CALLBACK REQUEST";
                    }).length,
                    email: this.historyItems.filter(function(item) {
                        return item.type === "EMAIL";
                    }).length,
                    fax: this.historyItems.filter(function(item) {
                        return item.type === "FAX";
                    }).length,
                    voicemail: this.historyItems.filter(function(item) {
                        return item.type === "VOICEMAIL";
                    }).length,
                };
            } catch (e) {
                if (
                    this.historyAccount &&
                    this.historyAccount.accountID === accountID
                ) {
                    this.errorMessage =
                        "Could not load communications for this account.";
                }
            } finally {
                if (
                    !this.historyAccount ||
                    this.historyAccount.accountID === accountID
                ) {
                    this.historyLoading = false;
                }
            }
        },
        historyPreview(item) {
            if (item && (item.type === "CALLBACK REQUEST" || item.type === "VOICEMAIL")) {
                var callbackPreview = this.callbackPreviewText(item);
                if (callbackPreview) {
                    if (callbackPreview.length > 140) {
                        return callbackPreview.substring(0, 140) + "...";
                    }
                    return callbackPreview;
                }
                return item.recording ? "Voicemail recording" : "No recording";
            }

            var text = (item && (item.subject || item.text)) || "";
            if (text.length > 140) {
                return text.substring(0, 140) + "...";
            }
            return text || "—";
        },
        selectItem(item) {
            this.selected = item;
            this.assignAccountID = "";
            this.assignAccountInfo = null;
            this.assignToUser = "";
            this.showReply = false;
            this.replyMode = "reply";
            this.replyBody = "";
            this.replyCc = "";
            this.replyBcc = "";
            this.relatedDialog = false;
            this.relatedItems = [];
            this.relatedCount = 0;
            this.closeForward();
            if (this.$refs.assignLookup && this.$refs.assignLookup.clear) {
                this.$refs.assignLookup.clear();
            }
            if (this.$refs.studentLookup && this.$refs.studentLookup.clear) {
                this.$refs.studentLookup.clear();
            }

            this.accountStudents = [];
            this.registrationRequirements = [];
            this.studentDialog = false;
            this.selectedStudent = null;

            if (item && item.accountID && !item.needsAccount) {
                this.loadAccountInfo(item.accountID);
                this.loadAccountStudents(item.accountID);
                this.loadRegistrationRequirements(item.accountID);
            }

            if (item && item.type === "EMAIL" && item.emailID && !item.bodyHtml) {
                this.loadInboxEmailBody(item);
            }
            this.loadRelated(item, true);
        },
        async loadInboxEmailBody(item) {
            try {
                const body = await this.$http.get(
                    "api/emails/getBody?emailID=" + item.emailID,
                );
                if (
                    !body ||
                    !this.selected ||
                    this.selected.communicationID !== item.communicationID
                ) {
                    return;
                }

                this.$set(item, "bodyHtml", body.bodyHtml || "");
                if (!item.text && body.text) {
                    this.$set(item, "text", body.text);
                }
                if (!item.cc && body.cc) {
                    this.$set(item, "cc", body.cc);
                }
                if (!item.bcc && body.bcc) {
                    this.$set(item, "bcc", body.bcc);
                }
            } catch (e) {
                // Keep the inbox row usable without the HTML body.
            }
        },
        applyAccountInfo(item, info) {
            if (!item || !info) {
                return;
            }

            this.$set(item, "registrationStatus", info.registrationStatus);
            this.$set(item, "missingRequirements", info.missingRequirements);
            this.$set(item, "studentClasses", info.studentClasses);
            this.$set(item, "students", info.students);
            this.$set(item, "tuitionBalance", info.tuitionBalance);
            this.$set(item, "transportationBalance", info.transportationBalance);
            this.$set(item, "activeSchedules", info.activeSchedules);
            this.$set(item, "totalScheduledAmount", info.totalScheduledAmount);
            this.$set(item, "nextScheduleDate", info.nextScheduleDate);
            this.$set(item, "lastSuccessfulSchedule", info.lastSuccessfulSchedule);

            var address = info.address || info.Address;
            if (address) {
                this.$set(item, "address", address);
            }
        },
        normalizeAccountId(id) {
            return id == null || String(id).trim() === "" ? "" : String(id).trim();
        },
        copyInboxEnrichment(fromItem, toItem) {
            if (!fromItem || !toItem) {
                return;
            }

            var fields = [
                "address",
                "bodyHtml",
                "registrationStatus",
                "missingRequirements",
                "studentClasses",
                "students",
                "tuitionBalance",
                "transportationBalance",
                "activeSchedules",
                "totalScheduledAmount",
                "nextScheduleDate",
                "lastSuccessfulSchedule",
            ];
            var i;
            for (i = 0; i < fields.length; i++) {
                var field = fields[i];
                var previous = fromItem[field];
                var next = toItem[field];
                if (
                    previous != null &&
                    previous !== "" &&
                    (next == null || next === "")
                ) {
                    this.$set(toItem, field, previous);
                }
            }
            if (fromItem.text && !toItem.text) {
                this.$set(toItem, "text", fromItem.text);
            }
        },
        adoptInboxSelection(previous, items) {
            if (!previous) {
                return null;
            }

            var selectedId = previous.communicationID;
            var next =
                (items || []).find(function(item) {
                    return item.communicationID === selectedId;
                }) || null;
            if (!next) {
                return null;
            }

            this.copyInboxEnrichment(previous, next);
            var cacheKey = this.normalizeAccountId(next.accountID);
            if (cacheKey && this.accountInfoCache[cacheKey]) {
                this.applyAccountInfo(next, this.accountInfoCache[cacheKey]);
            }
            return next;
        },
        async loadAccountInfo(accountID) {
            var key = (accountID || "").toString().trim();
            if (!key) {
                return;
            }

            if (this.accountInfoCache[key]) {
                if (
                    this.selected &&
                    this.normalizeAccountId(this.selected.accountID) === key
                ) {
                    this.applyAccountInfo(this.selected, this.accountInfoCache[key]);
                }
                return;
            }

            try {
                const info = await this.$http.get(
                    "api/communications/getInboxAccountInfo?accountID=" +
                        encodeURIComponent(key),
                );
                if (!info) {
                    return;
                }

                this.$set(this.accountInfoCache, key, info);
                if (
                    this.selected &&
                    this.normalizeAccountId(this.selected.accountID) === key
                ) {
                    this.applyAccountInfo(this.selected, info);
                }
            } catch (e) {
                // Keep the inbox usable without registration details.
            }
        },
        async loadAccountStudents(accountID) {
            this.accountStudentsLoading = true;
            try {
                const students = await this.$http.get(
                    "api/accounts/getStudents?accountID=" +
                        accountID +
                        "&currentRegistrationOnly=true",
                );
                if (this.selected && this.selected.accountID === accountID) {
                    this.accountStudents = students || [];
                }
            } catch (e) {
                if (this.selected && this.selected.accountID === accountID) {
                    this.accountStudents = [];
                }
            }
            this.accountStudentsLoading = false;
        },
        async loadRegistrationRequirements(accountID) {
            this.registrationLoading = true;
            try {
                const requirements = await this.$http.get(
                    "api/accounts/getRegistrationRequirments?accountID=" + accountID,
                );
                if (this.selected && this.selected.accountID === accountID) {
                    this.registrationRequirements = requirements || [];
                }
            } catch (e) {
                if (this.selected && this.selected.accountID === accountID) {
                    this.registrationRequirements = [];
                }
            }
            this.registrationLoading = false;
        },
        inboxItemName(item) {
            if (!item) return "Unknown account";
            if (item.name) return item.name;
            if (item.accountID && !item.needsAccount) return item.accountID;
            if (item.callerName) return item.callerName;
            if (item.type === "VOICEMAIL" && item.from) {
                return this.formatPhone(item.from) || item.from;
            }
            return "Unknown account";
        },
        typeLabel(type) {
            if (type === "CALLBACK REQUEST") return "Callback";
            if (type === "SMS") return "SMS";
            if (type === "EMAIL") return "Email";
            if (type === "FAX") return "Fax";
            if (type === "VOICEMAIL") return "Voicemail";
            return type;
        },
        typeIcon(type) {
            if (type === "SMS") return "mdi-message-text";
            if (type === "CALLBACK REQUEST") return "mdi-phone-return";
            if (type === "EMAIL") return "mdi-email";
            if (type === "FAX") return "mdi-fax";
            if (type === "VOICEMAIL") return "mdi-voicemail";
            return "mdi-message";
        },
        typeColor(type) {
            if (type === "SMS") return "#5c6bc0";
            if (type === "CALLBACK REQUEST") return "#fdb400";
            if (type === "EMAIL") return "#26a69a";
            if (type === "FAX") return "#8d6e63";
            if (type === "VOICEMAIL") return "#5e35b1";
            return "#607d8b";
        },
        regStatusColor(status) {
            var value = (status || "").toUpperCase();
            if (value.indexOf("PARTIAL") !== -1) {
                return "#fb8c00";
            }
            if (value.indexOf("REGISTERED") !== -1 && value.indexOf("NOT") === -1) {
                return "#4caf50";
            }
            if (value.indexOf("PENDING") !== -1) {
                return "#fdb400";
            }
            return "#e53935";
        },
        regStatusIcon(status) {
            var value = (status || "").toUpperCase();
            if (value.indexOf("PARTIAL") !== -1) {
                return "mdi-alert-circle-outline";
            }
            if (value.indexOf("REGISTERED") !== -1 && value.indexOf("NOT") === -1) {
                return "mdi-check-circle";
            }
            if (value.indexOf("PENDING") !== -1) {
                return "mdi-clock-outline";
            }
            return "mdi-close-circle";
        },
        isInboxUnassigned(assignedTo) {
            if (!assignedTo || !String(assignedTo).trim()) {
                return true;
            }
            return String(assignedTo).trim().toLowerCase() === "unassigned";
        },
        clampAssignedFilterForRole() {
            if (
                !this.isAdmin &&
                (this.filters.assignedFilter === "user" ||
                    this.filters.assignedFilter === "all")
            ) {
                this.filters.assignedFilter = "mineAndUnassigned";
                this.filters.assignedToUser = "";
            }
        },
        effectiveAssignedFilter() {
            this.clampAssignedFilterForRole();
            return this.filters.assignedFilter;
        },
        canSeeInboxAssignment(assignedTo) {
            if (this.isAdmin) {
                return true;
            }
            return this.isInboxUnassigned(assignedTo) || assignedTo === this.user;
        },
        restrictInboxItems(list) {
            if (this.isAdmin) {
                return list || [];
            }
            var self = this;
            return (list || []).filter(function(item) {
                return self.canSeeInboxAssignment(item && item.assignedTo);
            });
        },
        applyCompletedStatus(communicationID, note) {
            var self = this;
            function apply(target) {
                if (!target) {
                    return;
                }
                self.$set(target, "status", "COMPLETED");
                if (note != null) {
                    self.$set(target, "note", note);
                }
            }

            apply(
                this.items.find(function(item) {
                    return item.communicationID === communicationID;
                }),
            );
            if (this.selected && this.selected.communicationID === communicationID) {
                apply(this.selected);
            }
            apply(
                this.historyItems.find(function(item) {
                    return item.communicationID === communicationID;
                }),
            );
        },
        async waitForLocalInboxUpdate() {
            await this.$nextTick();
        },
        assignedChipColor(assignedTo) {
            if (this.isInboxUnassigned(assignedTo)) return "#cfd8dc";
            return assignedTo === this.user ? "#4caf50" : "#78909c";
        },
        assignedChipLabel(assignedTo) {
            if (this.isInboxUnassigned(assignedTo)) return "Unassigned";
            return assignedTo === this.user ? "Me" : assignedTo;
        },
        formatListDate(value) {
            if (!value) return "";
            return moment(value).format("MM/DD/YYYY");
        },
        formatListTime(value) {
            if (!value) return "";
            return moment(value).format("h:mm A");
        },
        formatPhone(value) {
            if (!value) return "—";
            if (value.length === 10 || value.length === 11) {
                return formatPhoneNumber(
                    value.length === 11 ? value.substring(1) : value,
                );
            }
            return value;
        },
        mailedByDomain(email) {
            if (!email) return "";
            var at = email.indexOf("@");
            if (at === -1) return "";
            return email.substring(at + 1).trim();
        },
        previewText(item) {
            if (item && (item.type === "CALLBACK REQUEST" || item.type === "VOICEMAIL")) {
                var callbackPreview = this.callbackPreviewText(item);
                if (callbackPreview) {
                    if (callbackPreview.length > 90) {
                        return callbackPreview.substring(0, 90) + "...";
                    }
                    return callbackPreview;
                }
                return item.recording ? "Voicemail recording" : "No recording";
            }

            var text = item.text || item.subject || "";
            if (text.length > 90) {
                return text.substring(0, 90) + "...";
            }
            return text;
        },
        transcriptionValue(text) {
            if (text == null) return "";
            var trimmed = (text + "").trim();
            if (!trimmed || trimmed === "-") return "";
            var stripped = this.stripHtml(trimmed);
            if (!stripped || stripped === "-") return "";
            return trimmed;
        },
        stripHtml(text) {
            if (!text) return "";
            return (text + "")
                .replace(/<[^>]+>/g, " ")
                .replace(/&nbsp;/gi, " ")
                .replace(/&amp;/gi, "&")
                .replace(/&lt;/gi, "<")
                .replace(/&gt;/gi, ">")
                .replace(/&quot;/gi, '"')
                .replace(/&#39;/g, "'")
                .replace(/\s+/g, " ")
                .trim();
        },
        escapeHtml(text) {
            return (text + "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
        },
        transcriptHtml(text) {
            if (!text) return "";
            if (/<[a-z][\s\S]*>/i.test(text)) {
                return text;
            }
            return this.escapeHtml(text).replace(/\r\n|\r|\n/g, "<br>");
        },
        callbackSummary(item) {
            return this.transcriptionValue(item && item.transcriptionSummary);
        },
        callbackTranscription(item) {
            return this.transcriptionValue(item && item.transcription);
        },
        callbackPreviewText(item) {
            return this.stripHtml(
                this.callbackSummary(item) || this.callbackTranscription(item),
            );
        },
        hasDistinctFullTranscript(item) {
            var full = this.stripHtml(this.callbackTranscription(item));
            var summary = this.stripHtml(this.callbackSummary(item));
            return !!full && full !== summary;
        },
        toggleFullTranscription(item) {
            if (!item) return;
            this.$set(item, "showFullTranscription", !item.showFullTranscription);
        },
        trimText(text) {
            if (!text) return text;
            // white-space: pre-line turns leading/trailing newlines into
            // blank lines, so trim the ends (internal line breaks stay intact).
            return text.trimStart().trimEnd();
        },
        showFromContact(item) {
            if (!item || !item.from) return false;
            if (!item.emailAddress) return true;

            return (
                item.from.trim().toLowerCase() !==
                item.emailAddress.trim().toLowerCase()
            );
        },
        faxPreviewRow(item) {
            return {
                id: item.faxID,
                fileName: item.fileName,
                text: item.text,
                hasAttachment: item.hasAttachment,
                accountID: item.needsAccount ? "" : item.accountID,
                accountName: item.needsAccount ? "" : item.name,
                subject: item.subject,
                date: item.date,
                communicationID: item.communicationID,
            };
        },
        voicemailPreviewRow(item) {
            return {
                id: item.voicemailID,
                from: item.from,
                to: item.to,
                callerName: item.callerName,
                phoneNumber: item.from,
                fileName: item.fileName,
                recording: item.recording,
                transcription: item.transcription,
                transcriptionSummary: item.transcriptionSummary,
                accountID: item.needsAccount ? "" : item.accountID,
                subject: item.subject,
                date: item.date,
                communicationID: item.communicationID,
            };
        },
        emailPreviewRow(item) {
            return {
                id: item.emailID || item.id,
                bodyHtml: item.bodyHtml,
                text: item.text,
                hasAttachments: this.hasEmailFiles(item),
                accountID: item.needsAccount ? "" : item.accountID,
                accountName: item.needsAccount ? "" : item.name,
                subject: item.subject,
                date: item.date,
                communicationID: item.communicationID,
            };
        },
        relatedPreviewRow(item) {
            return {
                id: item.id,
                bodyHtml: item.bodyHtml,
                text: item.text,
                hasAttachments: !!item.hasAttachments,
                accountID: item.accountID,
                subject: item.subject,
                date: item.date,
            };
        },
        relatedKindLabel(item) {
            var kind = ((item && item.sourceKind) || "").toUpperCase();
            if (kind === "FORWARD") {
                return "Forward";
            }
            if (kind === "REPLY") {
                return "Reply";
            }
            return "Outgoing";
        },
        relatedKindColor(item) {
            var kind = ((item && item.sourceKind) || "").toUpperCase();
            if (kind === "FORWARD") {
                return "#5c6bc0";
            }
            if (kind === "REPLY") {
                return "#43a047";
            }
            return "#78909c";
        },
        relatedQuery(item) {
            var params = [];
            if (item && item.emailID) {
                params.push("emailID=" + encodeURIComponent(item.emailID));
            }
            if (item && item.communicationID) {
                params.push("communicationID=" + encodeURIComponent(item.communicationID));
            }
            return params.join("&");
        },
        async loadRelated(item, silent) {
            if (!item || (!item.emailID && !item.communicationID)) {
                this.relatedItems = [];
                this.relatedCount = 0;
                return;
            }

            var query = this.relatedQuery(item);
            if (!query) {
                this.relatedItems = [];
                this.relatedCount = 0;
                return;
            }

            if (!silent) {
                this.relatedLoading = true;
            }

            try {
                const items = await this.$http.get("api/emails/getRelated?" + query);
                if (!this.selected || this.selected.communicationID !== item.communicationID) {
                    return;
                }
                this.relatedItems = (items || []).map(function(row) {
                    row.expanded = false;
                    return row;
                });
                this.relatedCount = this.relatedItems.length;
            } catch (e) {
                if (!silent) {
                    this.relatedItems = [];
                    this.relatedCount = 0;
                }
            }

            this.relatedLoading = false;
        },
        async openRelated() {
            if (!this.selected) {
                return;
            }
            this.relatedDialog = true;
            await this.loadRelated(this.selected, false);
        },
        toggleRelatedItem(item) {
            this.$set(item, "expanded", !item.expanded);
        },
        hasEmailFiles(item) {
            if (!item) return false;
            return !!(item.hasAttachment || item.hasAttachments || item.attachmentCount);
        },
        isApplicantStudent(student) {
            if (!student) return false;
            if (student.isApplicant === true || student.isApplicant === "true") {
                return true;
            }
            var status = ((student.status || "") + "").trim().toLowerCase();
            return status === "pending";
        },
        studentStatusTitle(student, statusOnly) {
            var status = "Not registered";
            if (!statusOnly && this.isApplicantStudent(student)) {
                status = "New Applicant Student";
            } else if (student && student.studentRegistered) {
                status = "Registered";
            }

            if (statusOnly) {
                return status;
            }

            var parts = [status];
            if (this.studentHasSkipcard(student)) {
                parts.push((student.skipcard || "").trim());
            }
            parts.push("Click for details");
            return parts.join(" · ");
        },
        studentStatusColor(student) {
            if (student && student.studentRegistered) {
                return "#43a047";
            }
            return "#e53935";
        },
        studentHasSkipcard(student) {
            return !!(student && student.skipcard && (student.skipcard + "").trim());
        },
        studentDisplayName(student) {
            if (!student) return "";
            return (
                student.engName ||
                student.hebName ||
                student.firstName ||
                (student.studentID || "").trim() ||
                "Student"
            );
        },
        openStudentDetails(student) {
            this.selectedStudent = student || null;
            this.studentDialog = !!student;
        },
        formatStudentDate(value) {
            if (!value) return "";
            var parsed = moment(value);
            if (!parsed.isValid() || parsed.year() < 1900) {
                return "";
            }
            return parsed.format("MM/DD/YYYY");
        },
        studentDetailRows(student) {
            if (!student) return [];

            var rows = [];
            var self = this;
            function add(label, value) {
                if (value === true) {
                    rows.push({ label: label, value: "Yes" });
                    return;
                }
                if (value === false) {
                    rows.push({ label: label, value: "No" });
                    return;
                }
                if (value === null || value === undefined || value === "") {
                    return;
                }
                rows.push({ label: label, value: value });
            }

            add("Student ID", ((student.studentID || "") + "").trim());
            add("Hebrew name", student.hebName);
            add("Last name", student.lastName || student.lastNameH);
            add("Class", student.class);
            add("Section", student.section);
            add("Building", student.building);
            add("Branch", student.branch);
            add("School", student.school);
            add("Status", student.status);
            add("Reg ID", student.regID);
            add("Date of birth", self.formatStudentDate(student.dob));
            add("Start date", self.formatStudentDate(student.startDate));
            add("Teacher", student.teacherName);
            add(
                "Teacher number",
                student.teacherNumber ? self.formatPhone(student.teacherNumber) : "",
            );
            add("Voucher status", student.voucherStatus);
            if (student.voucherStatus == null && student.voucher != null) {
                add("Voucher", student.voucher);
            }
            add(
                "Immunizations",
                student.missingImm
                    ? "Missing: " + student.missingImm
                    : student.immRegStatus || "OK",
            );
            if (student.missCH205 === true) {
                add("Missing CH205", "Yes");
            }
            if (student.books != null) {
                add("Books", student.books);
            }
            if (student.did) {
                add(
                    "Conference line",
                    self.formatPhone(student.did) +
                        (student.pin ? " - " + student.pin : ""),
                );
            }

            return rows;
        },
        studentMissingRequirements(student) {
            if (!student || !this.incompleteRequirements) {
                return [];
            }

            var key = ((student.studentID || "") + "").trim();
            return this.incompleteRequirements.filter(function(req) {
                return ((req.studentID || "") + "").trim() === key;
            });
        },
        toggleHistoryItem(item) {
            item.expanded = !item.expanded;
        },
        isHistoryPending(item) {
            return !!(item && item.status !== "COMPLETED");
        },
        isHistoryRowSelected(item) {
            return this.historySelectedIds.indexOf(item.communicationID) !== -1;
        },
        syncHistoryCompletePanel() {
            if (!this.historySelectedIds.length) {
                this.historyCompleteOpen = false;
            }
        },
        toggleHistorySelectAll(value) {
            if (value) {
                this.historySelectedIds = this.historyPendingItems.map(
                    (item) => item.communicationID,
                );
            } else {
                this.historySelectedIds = [];
            }
            this.syncHistoryCompletePanel();
        },
        toggleHistoryRowSelection(item) {
            if (!this.isHistoryPending(item)) {
                return;
            }

            var index = this.historySelectedIds.indexOf(item.communicationID);
            if (index === -1) {
                this.historySelectedIds.push(item.communicationID);
            } else {
                this.historySelectedIds.splice(index, 1);
            }
            this.syncHistoryCompletePanel();
        },
        openHistoryCompleteDialog() {
            if (!this.historySelectedIds.length) {
                return;
            }

            this.bulkNote = "";
            this.historyNoteReady = false;
            this.historyCompleteOpen = true;
        },
        cancelHistoryComplete() {
            this.historyCompleteOpen = false;
            this.completingFromHistory = false;
        },
        async confirmHistoryComplete() {
            this.bulkNote = this.readIsolatedText("historyNote", this.bulkNote);
            if (!this.bulkNote || !this.bulkNote.trim()) {
                this.$notify({
                    group: "notification",
                    text:
                        "You can't mark as completed without entering a note.",
                    type: "error",
                });
                return;
            }

            var self = this;
            var ids = this.historySelectedIds.filter(function(id) {
                var item = self.historyItems.find(function(row) {
                    return row.communicationID === id;
                });
                return item && item.status !== "COMPLETED";
            });
            if (!ids.length) {
                return;
            }

            this.selectedIds = ids;
            this.completingFromHistory = true;
            await this.confirmBulkUpdate();
        },
        requirementStudentName(req) {
            if (!req || !req.studentID) return "";

            var key = (req.studentID || "").trim();
            var student = this.accountStudents.find(function(s) {
                return ((s.studentID || "") + "").trim() === key;
            });

            return student ? student.engName || student.hebName || "" : "";
        },
        requirementExtraInfo(req) {
            if (!req) return "";

            var type = ((req.docTypeID || "") + " " + (req.reqType || "")).toUpperCase();
            var isVotingOrImm =
                type.indexOf("VOT") !== -1 || type.indexOf("IMM") !== -1;

            if (!isVotingOrImm) {
                return "";
            }

            if (req.description) {
                return req.description;
            }

            var student = this.accountStudents.find(function(s) {
                return (
                    req.studentID &&
                    ((s.studentID || "") + "").trim() === (req.studentID || "").trim()
                );
            });

            return (student && (student.missingImm || student.immRegStatus)) || "";
        },
        openAccount(item) {
            if (!item.accountID || item.needsAccount) {
                return;
            }

            var query = { hideNav: "true" };
            if (item.type === "SMS") {
                query.goToMessageSection = true;
            } else if (item.type === "CALLBACK REQUEST") {
                query.goToCallbackSection = true;
            } else if (item.type === "EMAIL") {
                query.goToEmailsSection = true;
            } else if (item.type === "FAX") {
                query.goToFaxesSection = true;
            } else if (item.type === "VOICEMAIL") {
                query.goToVoicemailsSection = true;
            }

            var route = this.$router.resolve({
                name: "Account View",
                params: { id: (item.accountID || "").trim() },
                query: query,
            });
            this.openAccountWindow(route.href);
        },
        openAccountWindow(url) {
            var availW = window.screen.availWidth || 1280;
            var availH = window.screen.availHeight || 800;
            var width = Math.min(1280, Math.max(980, Math.round(availW * 0.7)));
            var height = Math.min(880, Math.max(720, Math.round(availH * 0.78)));
            width = Math.min(width, availW - 40);
            height = Math.min(height, availH - 60);

            var left = Math.round(
                window.screenX + (window.outerWidth - width) / 2,
            );
            var top = Math.round(
                window.screenY + Math.max(36, (window.outerHeight - height) / 4),
            );

            var opened = window.open(
                url,
                "utaAccountView",
                "popup=yes,width=" +
                    width +
                    ",height=" +
                    height +
                    ",left=" +
                    left +
                    ",top=" +
                    top +
                    ",resizable=yes,scrollbars=yes",
            );
            if (opened && opened.focus) {
                opened.focus();
            }
        },
        onAssignAccountSelected(account) {
            this.assignAccountInfo = account;
            if (account && this.$refs.studentLookup && this.$refs.studentLookup.clearSelection) {
                this.$refs.studentLookup.clearSelection();
            }
        },
        onAssignStudentSelected(account) {
            this.assignAccountInfo = account;
            if (account && this.$refs.assignLookup && this.$refs.assignLookup.clearSelection) {
                this.$refs.assignLookup.clearSelection();
            }
        },
        applyLinkedAccount(item, account) {
            if (!item) {
                return;
            }

            var accountID = ((account && account.accountID) || this.assignAccountID || "")
                .toString()
                .trim();
            this.$set(item, "needsAccount", false);
            this.$set(item, "accountID", accountID);

            if (account) {
                if (account.name) {
                    this.$set(item, "name", account.name);
                }
                if (account.hname) {
                    this.$set(item, "nameH", account.hname);
                }
                if (account.emailAddress) {
                    this.$set(item, "emailAddress", account.emailAddress);
                }
                if (account.address1) {
                    this.$set(item, "address", account.address1);
                }
            }
        },
        async assignAccount() {
            if (!this.selected || !this.assignAccountID) {
                return;
            }

            this.processing = true;

            try {
                await this.$http.post("api/communications/assignAccount", {
                    type: this.selected.type,
                    emailID: this.selected.emailID,
                    faxID: this.selected.faxID,
                    voicemailID: this.selected.voicemailID,
                    smsID: this.selected.smsID,
                    callbackRequestID: this.selected.callbackRequestID,
                    accountID: this.assignAccountID,
                    user: this.user,
                });

                this.applyLinkedAccount(this.selected, this.assignAccountInfo);

                this.assignAccountID = "";
                this.assignAccountInfo = null;
                if (this.$refs.assignLookup && this.$refs.assignLookup.clear) {
                    this.$refs.assignLookup.clear();
                }
                if (this.$refs.studentLookup && this.$refs.studentLookup.clear) {
                    this.$refs.studentLookup.clear();
                }

                this.$notify({
                    group: "notification",
                    text: "Account assigned",
                    type: "success",
                });
                this.processing = false;

                if (this.selected && this.selected.accountID) {
                    this.loadAccountInfo(this.selected.accountID);
                    this.loadAccountStudents(this.selected.accountID);
                    this.loadRegistrationRequirements(this.selected.accountID);
                }

                this.loadInbox(true);
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text: "Could not assign account",
                    type: "error",
                });
                this.processing = false;
            }
        },
        openUnlinkAccountDialog() {
            if (!this.selected || this.selected.needsAccount) {
                return;
            }
            this.unlinkAccountDialog = true;
        },
        applyUnlinkedAccount(item) {
            if (!item) {
                return;
            }

            this.$set(item, "needsAccount", true);
            this.$set(item, "accountID", "");
            this.$set(item, "name", "");
            this.$set(item, "nameH", "");
            this.$set(item, "emailAddress", "");
            this.$set(item, "address", "");
            this.$set(item, "registrationStatus", "");
            this.$set(item, "missingRequirements", "");
            this.$set(item, "studentClasses", "");
            this.$set(item, "students", null);
            this.$set(item, "tuitionBalance", null);
            this.$set(item, "transportationBalance", null);
            this.$set(item, "activeSchedules", null);
            this.$set(item, "totalScheduledAmount", null);
            this.$set(item, "nextScheduleDate", null);
            this.$set(item, "lastSuccessfulSchedule", null);
            this.accountStudents = [];
            this.registrationRequirements = [];
        },
        async confirmUnlinkAccount() {
            if (!this.selected || this.selected.needsAccount) {
                return;
            }

            this.unlinkingAccount = true;

            try {
                await this.$http.post(
                    "api/communications/unlinkAccount",
                    {
                        type: this.selected.type,
                        emailID: this.selected.emailID,
                        faxID: this.selected.faxID,
                        voicemailID: this.selected.voicemailID,
                        smsID: this.selected.smsID,
                        callbackRequestID: this.selected.callbackRequestID,
                        user: this.user,
                    },
                    { notifyError: false },
                );

                this.applyUnlinkedAccount(this.selected);

                this.unlinkAccountDialog = false;
                this.$notify({
                    group: "notification",
                    text: "Account unlinked",
                    type: "success",
                });
                this.unlinkingAccount = false;
                this.loadInbox(true);
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text: "Could not unlink account",
                    type: "error",
                });
                this.unlinkingAccount = false;
            }
        },
        async assignToMe(item) {
            await this.assignUser(item, this.user);
        },
        async unassignUser(item) {
            await this.assignUser(item, "");
        },
        async assignToOther(item) {
            if (!this.assignToUser) {
                return;
            }
            await this.assignUser(item, this.assignToUser);
        },
        applyAssignedUser(item, assignedTo) {
            if (!item) {
                return;
            }

            var nextAssignedTo = assignedTo || "";
            this.$set(item, "assignedTo", nextAssignedTo);

            if (
                this.selected &&
                this.selected.communicationID === item.communicationID
            ) {
                this.$set(this.selected, "assignedTo", nextAssignedTo);
            }
        },
        async assignUser(item, assignedTo) {
            if (!item) return;

            this.processing = true;

            try {
                await this.$http.post(
                    "api/communications/assignCommunicationUser",
                    {
                        communicationID: item.communicationID,
                        user: this.user,
                        assignedTo: assignedTo,
                    },
                );

                this.applyAssignedUser(item, assignedTo);
                this.assignToUser = "";

                this.$notify({
                    group: "notification",
                    text: assignedTo
                        ? "Assigned to " + (assignedTo === this.user ? "you" : assignedTo)
                        : "Unassigned",
                    type: "success",
                });
                this.processing = false;
                this.loadInbox(true);
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text:
                        (e && e.message) ||
                        "Could not update the assignment",
                    type: "error",
                });
                this.processing = false;
            }
        },
        toggleSelectAll(value) {
            if (value) {
                this.selectedIds = this.filteredItems.map(
                    (item) => item.communicationID,
                );
            } else {
                this.selectedIds = [];
            }
        },
        isRowSelected(item) {
            return this.selectedIds.indexOf(item.communicationID) !== -1;
        },
        toggleRowSelection(item) {
            var index = this.selectedIds.indexOf(item.communicationID);
            if (index === -1) {
                this.selectedIds.push(item.communicationID);
            } else {
                this.selectedIds.splice(index, 1);
            }
        },
        clearSelection() {
            this.selectedIds = [];
        },
        async bulkPickup() {
            await this.bulkAssign(this.user);
        },
        async bulkUnassign() {
            await this.bulkAssign("");
        },
        async bulkAssign(assignedTo) {
            if (!this.selectedIds.length) return;

            this.bulkProcessing = true;

            var ids = this.selectedIds.slice();
            var self = this;
            var results = await Promise.all(
                ids.map((id) =>
                    this.$http
                        .post("api/communications/assignCommunicationUser", {
                            communicationID: id,
                            user: this.user,
                            assignedTo: assignedTo,
                        })
                        .then(function() {
                            var item = self.items.find(function(row) {
                                return row.communicationID === id;
                            });
                            self.applyAssignedUser(item, assignedTo);
                            return true;
                        })
                        .catch(() => false),
                ),
            );

            var successCount = results.filter((result) => result).length;
            var failCount = results.length - successCount;

            this.$notify({
                group: "notification",
                text:
                    successCount +
                    (assignedTo ? " assigned to " + (assignedTo === this.user ? "you" : assignedTo) : " unassigned") +
                    (failCount
                        ? ", " + failCount + " skipped (already assigned to someone else)"
                        : ""),
                type: failCount ? "warning" : "success",
            });

            this.selectedIds = [];
            this.bulkProcessing = false;
            await this.loadInbox(true);
        },
        openBulkUpdateDialog() {
            if (!this.selectedIds.length) return;

            this.completingSingle = false;
            this.bulkNote = "";
            this.completeNoteReady = false;
            this.bulkUpdateDialog = true;
        },
        openCompleteDialog() {
            if (!this.selected) return;

            this.completingSingle = true;
            this.selectedIds = [this.selected.communicationID];
            this.bulkNote = "";
            this.completeNoteReady = false;
            this.bulkUpdateDialog = true;
        },
        openManageFilesDialog(payload) {
            if (!payload || !payload.source || !payload.sourceId) {
                return;
            }

            this.manageFilesClosePrompt = false;
            this.manageFilesCloseNote = "";
            this.manageFiles = {
                open: true,
                source: payload.source,
                sourceId: payload.sourceId,
                communicationID: payload.communicationID || null,
                didSave: false,
            };
        },
        onManageFilesSaved() {
            this.manageFiles.didSave = true;
        },
        closeManageFiles() {
            if (this.manageFiles.didSave && this.canMarkManageFilesDone) {
                this.manageFilesCloseNote = "Document imported";
                this.manageFilesClosePrompt = true;
                return;
            }

            this.dismissManageFiles();
        },
        dismissManageFiles() {
            this.manageFilesClosePrompt = false;
            this.manageFilesCloseNote = "";
            this.manageFiles = {
                open: false,
                source: "",
                sourceId: "",
                communicationID: null,
                didSave: false,
            };
        },
        openCompleteFromManageFiles() {
            var item = this.manageFilesItem;
            if (!item || item.status === "COMPLETED") {
                return;
            }

            this.completingSingle = true;
            this.selectedIds = [item.communicationID];
            this.bulkNote = this.manageFiles.didSave ? "Document imported" : "";
            this.completeNoteReady = !!this.bulkNote.trim();
            this.bulkUpdateDialog = true;
        },
        async closeAndMarkManageFilesDone() {
            var item = this.manageFilesItem;
            if (!item || item.status === "COMPLETED") {
                this.dismissManageFiles();
                return;
            }
            this.manageFilesCloseNote = this.readIsolatedText(
                "manageFilesNote",
                this.manageFilesCloseNote,
            );
            if (!this.manageFilesCloseNote || !this.manageFilesCloseNote.trim()) {
                this.$notify({
                    group: "notification",
                    text: "You can't mark as completed without entering a note.",
                    type: "error",
                });
                return;
            }

            this.completingSingle = true;
            this.selectedIds = [item.communicationID];
            this.bulkNote = this.manageFilesCloseNote;
            this.manageFilesClosePrompt = false;
            await this.confirmBulkUpdate();
        },
        toggleContextPanel() {
            this.contextPanelCollapsed = !this.contextPanelCollapsed;
            this.persistFilters();
        },
        openReply(replyAll) {
            if (!this.selected || this.selected.type !== "EMAIL" || !this.selected.emailID) {
                return;
            }

            this.closeForward(true);
            this.replyMode = replyAll ? "reply-all" : "reply";
            this.replyCc = replyAll ? this.buildReplyAllCc(this.selected) : "";
            this.replyBcc = "";
            this.showReply = true;
            var self = this;
            this.$nextTick(function() {
                var el = self.$el.querySelector(".inbox-reply-box");
                if (el && el.scrollIntoView) {
                    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
                var focusSelector = replyAll ? "input" : "textarea";
                var field = el && el.querySelector(focusSelector);
                if (field && field.focus) {
                    field.focus();
                }
            });
        },
        closeReply(keepPosition) {
            this.showReply = false;
            this.replyMode = "reply";
            this.replyCc = "";
            this.replyBcc = "";
            if (keepPosition !== true) {
                this.scrollDetailToTop();
            }
        },
        scrollDetailToTop() {
            var self = this;
            this.$nextTick(function() {
                var el = self.$el.querySelector(".inbox-detail-scroll");
                if (el && el.scrollTo) {
                    el.scrollTo({ top: 0, behavior: "smooth" });
                } else if (el) {
                    el.scrollTop = 0;
                }
            });
        },
        extractEmailAddress(value) {
            if (!value) {
                return "";
            }
            var match = String(value).match(/[^\s<>,;]+@[^\s<>,;]+/);
            return match ? match[0] : "";
        },
        isMailboxAddress(value) {
            var email = (this.extractEmailAddress(value) || "").toLowerCase();
            return (
                email === "tuitionemailbox@sms.utaw.org" ||
                email === "tuition@utaw.org" ||
                email === "fax@sms.utaw.org" ||
                email.slice(-12) === "@sms.utaw.org"
            );
        },
        buildReplyAllCc(item) {
            if (!item) {
                return "";
            }

            var seen = {};
            var from = this.extractEmailAddress(item.from);
            if (from) {
                seen[from.toLowerCase()] = true;
            }

            var result = [];
            var self = this;
            var addList = function(value) {
                self.parseEmailList(value).forEach(function(part) {
                    var email = self.extractEmailAddress(part);
                    if (!email) {
                        return;
                    }
                    var key = email.toLowerCase();
                    if (seen[key] || self.isMailboxAddress(email)) {
                        return;
                    }
                    seen[key] = true;
                    result.push(part);
                });
            };

            addList(item.cc);
            addList(item.to);
            return result.join(", ");
        },
        parseEmailList(value) {
            return (value || "")
                .split(/[,;]/)
                .map(function(part) {
                    return part.trim();
                })
                .filter(function(part) {
                    return !!part;
                });
        },
        isValidEmail(value) {
            var email = this.extractEmailAddress(value);
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
        },
        validateEmailField(value, label, required) {
            var addresses = this.parseEmailList(value);
            if (required && !addresses.length) {
                this.$notify({
                    group: "notification",
                    text: label + " is required",
                    type: "error",
                });
                return false;
            }
            var self = this;
            var invalid = addresses.filter(function(address) {
                return !self.isValidEmail(address);
            });
            if (invalid.length) {
                this.$notify({
                    group: "notification",
                    text: "Invalid " + label + " address",
                    type: "error",
                });
                return false;
            }
            return true;
        },
        readIsolatedText(refName, fallback) {
            var field = this.$refs[refName];
            if (Array.isArray(field)) {
                field = field[0];
            }
            if (field && field.getValue) {
                return field.getValue();
            }
            return fallback || "";
        },
        async sendReply(payload) {
            if (!this.selected || !this.selected.emailID) {
                return;
            }
            var body = payload && payload.body != null ? payload.body : this.replyBody;
            var to = (payload && payload.to) || this.selected.from;
            var cc = payload && payload.cc != null ? payload.cc : this.replyCc;
            var bcc = payload && payload.bcc != null ? payload.bcc : this.replyBcc;
            var attachments =
                payload && payload.attachments ? payload.attachments : [];
            if (!body || !body.trim()) {
                return;
            }
            if (
                !this.validateEmailField(to, "To", true) ||
                !this.validateEmailField(cc, "Cc", false) ||
                !this.validateEmailField(bcc, "Bcc", false)
            ) {
                return;
            }

            this.sendingReply = true;

            try {
                await this.$http.post("api/emails/reply", {
                    emailID: this.selected.emailID,
                    to: to,
                    body: body,
                    cc: cc,
                    bcc: bcc,
                    note: body,
                    user: this.user,
                    markCompleted: true,
                    communicationID: this.selected.communicationID,
                    attachments: attachments,
                }, { notifyError: false, timeout: 120000 });
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text: (e && e.message) || "Could not send reply",
                    type: "error",
                });
                this.sendingReply = false;
                return;
            }

            this.applyCompletedStatus(
                this.selected.communicationID,
                body,
            );
            this.$notify({
                group: "notification",
                text: "Reply sent",
                type: "success",
            });
            this.closeReply();
            this.replyBody = "";
            try {
                await this.waitForLocalInboxUpdate();
                this.loadInbox(true);
            } catch (e) {
            }

            this.sendingReply = false;
            this.loadRelated(this.selected, true);
        },
        openForward() {
            if (!this.selected) {
                return;
            }

            this.closeReply(true);
            this.forwardAttachments = [];
            this.forwardAttachmentsLoaded = false;
            this.showForward = true;
            this.forwardTo = "";
            this.forwardCc = "";
            this.forwardBcc = "";
            this.forwardSubject = this.buildForwardSubject(this.selected);
            this.forwardBody =
                this.selected.type === "EMAIL"
                    ? ""
                    : this.buildForwardBody(this.selected);
            if (this.selected.type === "EMAIL" && this.selected.emailID) {
                this.loadForwardAttachments(this.selected.emailID);
            }

            var self = this;
            this.$nextTick(function() {
                var el = self.$el.querySelector(".inbox-reply-box");
                if (el && el.scrollIntoView) {
                    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
                var input = el && el.querySelector("input");
                if (input && input.focus) {
                    input.focus();
                }
            });
        },
        closeForward(keepPosition) {
            this.showForward = false;
            this.forwardTo = "";
            this.forwardCc = "";
            this.forwardBcc = "";
            this.forwardSubject = "";
            this.forwardBody = "";
            this.forwardAttachments = [];
            this.forwardAttachmentsLoaded = false;
            if (keepPosition !== true) {
                this.scrollDetailToTop();
            }
        },
        async loadForwardAttachments(emailID) {
            try {
                var files = await this.$http.get(
                    "api/emails/getAttachments?emailID=" + emailID,
                );
                if (
                    !this.showForward ||
                    !this.selected ||
                    this.selected.emailID !== emailID
                ) {
                    return;
                }
                var list = files || [];
                this.forwardAttachments = list.map(function(file) {
                    return {
                        attachmentId: file.id || file.ID,
                        fileName: file.fileName || file.FileName || "attachment",
                        contentType:
                            file.contentType ||
                            file.ContentType ||
                            "application/octet-stream",
                        fileURL: file.fileURL || file.FileURL || "",
                    };
                }).filter(function(file) {
                    return !!file.attachmentId;
                });
                this.forwardAttachmentsLoaded = true;
            } catch (e) {
                if (this.showForward && this.selected && this.selected.emailID === emailID) {
                    this.forwardAttachmentsLoaded = false;
                }
            }
        },
        buildForwardSubject(item) {
            if (!item) {
                return "Forwarded communication";
            }
            if (item.type === "EMAIL") {
                return item.subject || "Forwarded email";
            }
            if (item.type === "SMS") {
                return "SMS from " + (this.formatPhone(item.from) || item.from || "unknown");
            }
            if (item.type === "CALLBACK REQUEST") {
                return "Callback request from " + (this.formatPhone(item.from) || item.from || "unknown");
            }
            if (item.type === "FAX") {
                return item.subject || "Forwarded fax";
            }
            if (item.type === "VOICEMAIL") {
                return "Voicemail from " + (this.formatPhone(item.from) || item.from || "unknown");
            }
            return "Forwarded communication";
        },
        buildForwardBody(item) {
            if (!item) {
                return "";
            }

            var lines = [
                "---------- Forwarded " + this.typeLabel(item.type) + " ---------",
                "From: " +
                    (item.type === "EMAIL"
                        ? item.from || ""
                        : this.formatPhone(item.from) || item.from || ""),
                "Date: " +
                    this.formatListDate(item.date) +
                    " " +
                    this.formatListTime(item.date),
            ];

            if (item.accountID && !item.needsAccount) {
                lines.push("Account: " + item.accountID);
            }
            if (item.subject && item.type !== "SMS") {
                lines.push("Subject: " + item.subject);
            }
            if (item.type === "CALLBACK REQUEST" && item.callbackNumber) {
                lines.push("Callback number: " + this.formatPhone(item.callbackNumber));
            }

            lines.push("");
            if (item.type === "CALLBACK REQUEST" || item.type === "VOICEMAIL") {
                var summary = this.callbackSummary(item);
                var transcript = this.callbackTranscription(item);
                if (summary) {
                    lines.push("Summary:");
                    lines.push(summary);
                    lines.push("");
                }
                if (transcript) {
                    lines.push("Transcript:");
                    lines.push(transcript);
                } else if (item.recording) {
                    lines.push("Recording: " + item.recording);
                }
            } else {
                lines.push(this.trimText(item.text) || "");
            }

            return lines.join("\n").trim();
        },
        async sendForward(payload) {
            if (!this.selected) {
                return;
            }

            var to =
                payload && payload.to != null ? payload.to : this.forwardTo;
            var cc =
                payload && payload.cc != null ? payload.cc : this.forwardCc;
            var bcc =
                payload && payload.bcc != null ? payload.bcc : this.forwardBcc;
            var subject =
                payload && payload.subject != null
                    ? payload.subject
                    : this.forwardSubject;
            var body =
                payload && payload.body != null ? payload.body : this.forwardBody;
            var attachments =
                payload && payload.attachments ? payload.attachments : [];

            if (
                !this.validateEmailField(to, "To", true) ||
                !this.validateEmailField(cc, "Cc", false) ||
                !this.validateEmailField(bcc, "Bcc", false)
            ) {
                return;
            }
            if (
                this.selected.type !== "EMAIL" &&
                (!body || !body.trim())
            ) {
                return;
            }

            this.sendingForward = true;

            try {
                var postBody = {
                    to: (to || "").trim(),
                    cc: cc,
                    bcc: bcc,
                    subject: subject,
                    body: body,
                    accountID: this.selected.needsAccount
                        ? ""
                        : this.normalizeAccountId(this.selected.accountID),
                    user: this.user,
                    communicationID: this.selected.communicationID,
                    attachments: attachments,
                };
                if (this.selected.type === "EMAIL" && this.selected.emailID) {
                    postBody.emailID = this.selected.emailID;
                    if (this.forwardAttachmentsLoaded) {
                        postBody.originalAttachmentIDs = attachments
                            .filter(function(file) {
                                return file && file.attachmentId;
                            })
                            .map(function(file) {
                                return file.attachmentId;
                            });
                    }
                }

                await this.$http.post("api/emails/forward", postBody);
                this.$notify({
                    group: "notification",
                    text: "Forwarded",
                    type: "success",
                });
                this.closeForward();
                this.loadRelated(this.selected, true);
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text: "Could not forward communication",
                    type: "error",
                });
            }

            this.sendingForward = false;
        },
        cancelBulkUpdateDialog() {
            this.bulkUpdateDialog = false;

            if (this.completingSingle) {
                this.selectedIds = [];
                this.completingSingle = false;
            }
        },
        async confirmBulkUpdate() {
            this.bulkNote = this.readIsolatedText("completeNote", this.bulkNote);
            if (!this.bulkNote || !this.bulkNote.trim()) {
                this.$notify({
                    group: "notification",
                    text:
                        "You can't mark as completed without entering a note.",
                    type: "error",
                });
                return;
            }

            this.bulkProcessing = true;

            var ids = this.selectedIds.slice();
            var note = this.bulkNote;
            var self = this;
            var results = await Promise.all(
                ids.map((id) =>
                    this.$http
                        .post("api/communications/updateStatus", {
                            communicationID: id,
                            status: "COMPLETED",
                            note: note,
                            user: this.user,
                        })
                        .then(function() {
                            self.applyCompletedStatus(id, note);
                            return true;
                        })
                        .catch(() => false),
                ),
            );

            var successCount = results.filter((result) => result).length;
            var failCount = results.length - successCount;

            await this.waitForLocalInboxUpdate();

            this.$notify({
                group: "notification",
                text:
                    successCount +
                    " marked done" +
                    (failCount ? ", " + failCount + " failed" : ""),
                type: failCount ? "warning" : "success",
            });

            this.bulkUpdateDialog = false;
            this.selectedIds = [];
            this.completingSingle = false;
            if (this.completingFromHistory) {
                this.historySelectedIds = [];
                this.historyCompleteOpen = false;
                this.completingFromHistory = false;
            }

            var manageItem = this.manageFilesItem;
            if (
                this.manageFiles.open &&
                successCount &&
                manageItem &&
                ids.indexOf(manageItem.communicationID) !== -1
            ) {
                this.dismissManageFiles();
            }

            this.bulkProcessing = false;
            this.loadInbox(true);
        },
        openBalanceDialog() {
            if (!this.selected) return;

            this.balanceDialog = true;
            this.showFurtherBilling = false;
            this.futureBalanceTransactions = [];
            this.loadBalanceTransactions();
        },
        async loadBalanceTransactions() {
            if (!this.selected || !this.selected.accountID) return;

            this.balanceDetailsLoading = true;
            try {
                const details = await this.$http.get(
                    "api/accounts/getTransactionDetails?accountID=" +
                        this.selected.accountID,
                );
                this.balanceTransactions = details || [];
                this.calculateBalanceTransactions();
            } catch (e) {
                this.balanceTransactions = [];
                this.balanceTransactionsTotal = 0;
            }
            this.balanceDetailsLoading = false;
        },
        calculateBalanceTransactions() {
            let totalBalance = 0;
            this.balanceTransactions = this.balanceTransactions.map((transaction) => {
                let balance = totalBalance;
                if (transaction.description !== "Scheduled Payment") {
                    balance =
                        totalBalance +
                        (transaction.txnType === "D"
                            ? transaction.amount
                            : -transaction.amount);
                }

                totalBalance = balance;
                return { ...transaction, balance: balance };
            });

            this.balanceTransactionsTotal = totalBalance;
        },
        async loadFurtherBilling(value) {
            if (!value) {
                this.futureBalanceTransactions = [];
                this.calculateBalanceTransactions();
                return;
            }

            if (!this.selected || !this.selected.accountID) return;

            let futureTransactions = await this.$http.get(
                "api/accounts/getFurtherTransactions?accountID=" +
                    this.selected.accountID,
            );

            let totalBalance = this.balanceTransactionsTotal;
            this.futureBalanceTransactions = (futureTransactions || []).map(
                (transaction) => {
                    const balance =
                        totalBalance +
                        (transaction.type === "transaction"
                            ? transaction.amount
                            : -transaction.amount);

                    totalBalance = balance;
                    return { ...transaction, balance: balance };
                },
            );

            this.balanceTransactionsTotal = totalBalance;
        },
        firebaseSetup() {
            const firebaseConfig = {
                apiKey: "AIzaSyCtYeCL8HkqlMKJh6g6jcDvoH60-woVbDE",
                authDomain: "utaw.firebaseapp.com",
                databaseURL: "https://utaw.firebaseio.com",
                projectId: "project-683582246459980907",
                storageBucket: "project-683582246459980907.appspot.com",
                messagingSenderId: "193425158146",
                appId: "1:193425158146:web:f1ae134a803fb75832371b",
            };

            if (!firebase.apps.length) {
                firebase.initializeApp(firebaseConfig);
            }

            var self = this;
            this.firebaseNavRef = firebase.database().ref("navigation");
            this.firebaseInboxRef = firebase
                .database()
                .ref("incoming-communications/inbox");

            this.firebaseNavRef.on("value", function() {
                if (!self.firebaseNavReady) {
                    self.firebaseNavReady = true;
                    return;
                }
                self.scheduleInboxReload();
            });
            this.firebaseInboxRef.on("value", function() {
                if (!self.firebaseInboxReady) {
                    self.firebaseInboxReady = true;
                    return;
                }
                self.scheduleInboxReload();
            });
        },
        scheduleInboxReload() {
            var minInterval = 15000;
            var remaining = minInterval - (Date.now() - this.lastInboxLoadAt);

            if (remaining <= 0) {
                this.loadInbox({ silent: true, quietErrors: true });
                return;
            }

            if (this.pendingInboxReloadTimeout) {
                return;
            }

            var self = this;
            this.pendingInboxReloadTimeout = setTimeout(function() {
                self.pendingInboxReloadTimeout = null;
                self.loadInbox({ silent: true, quietErrors: true });
            }, remaining);
        },
        bindInboxVisibility() {
            var self = this;
            this.inboxVisibilityHandler = function() {
                if (document.visibilityState === "visible") {
                    self.onInboxTabVisible();
                }
            };
            document.addEventListener("visibilitychange", this.inboxVisibilityHandler);
        },
        unbindInboxVisibility() {
            if (this.inboxVisibilityHandler) {
                document.removeEventListener(
                    "visibilitychange",
                    this.inboxVisibilityHandler,
                );
                this.inboxVisibilityHandler = null;
            }
        },
        onInboxTabVisible() {
            this.refreshSelectedCommunication();
            this.loadInbox({ silent: true, quietErrors: true });
        },
        async refreshSelectedCommunication() {
            var selected = this.selected;
            if (
                !selected ||
                !selected.communicationID ||
                !selected.accountID ||
                selected.needsAccount
            ) {
                return;
            }

            var communicationID = selected.communicationID;
            var accountID = selected.accountID;

            try {
                var response = await this.$http.get(
                    "api/communications/getRecentForAccount?accountID=" +
                        encodeURIComponent(accountID),
                    { notifyError: false },
                );

                if (
                    !this.selected ||
                    this.selected.communicationID !== communicationID
                ) {
                    return;
                }

                var historyItems = (response && response.items) || [];
                var updated = historyItems.find(function(item) {
                    return item.communicationID === communicationID;
                });
                if (!updated) {
                    return;
                }

                var current = this.items.find(function(item) {
                    return item.communicationID === communicationID;
                });
                if (current) {
                    this.$set(current, "status", updated.status);
                    if (updated.note != null) {
                        this.$set(current, "note", updated.note);
                    }
                    if (updated.transcription != null) {
                        this.$set(current, "transcription", updated.transcription);
                    }
                    if (updated.transcriptionSummary != null) {
                        this.$set(current, "transcriptionSummary", updated.transcriptionSummary);
                    }
                }
                this.$set(this.selected, "status", updated.status);
                if (updated.note != null) {
                    this.$set(this.selected, "note", updated.note);
                }
                if (updated.transcription != null) {
                    this.$set(this.selected, "transcription", updated.transcription);
                }
                if (updated.transcriptionSummary != null) {
                    this.$set(this.selected, "transcriptionSummary", updated.transcriptionSummary);
                }
            } catch (e) {
                // keep the existing selected item until the full inbox reload finishes
            }
        },
    },
};
</script>
<style>
body.inbox-fill-height .application,
body.inbox-fill-height .application--wrap,
body.inbox-fill-height .v-content,
body.inbox-fill-height .v-content__wrap,
body.inbox-fill-height #core-view.core-view--inbox {
    height: 100vh;
    max-height: 100vh;
    min-height: 0;
}

body.inbox-fill-height #core-view.core-view--inbox {
    padding-bottom: 0;
    overflow: hidden;
}

.communications-inbox {
    padding: 12px 16px;
    height: 100vh;
    margin-bottom: -100px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

body.inbox-fill-height .communications-inbox {
    height: 100%;
    margin-bottom: 0;
}

.inbox-root-card {
    flex: 1 1 auto;
    display: flex !important;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    overflow: hidden;
}

.inbox-header {
    background-color: #4caf50;
    min-height: 0;
    flex: 0 0 auto;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    font-size: 16px;
}

.inbox-live {
    display: flex;
    align-items: center;
}

.inbox-live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #fff;
    margin-right: 6px;
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
    animation: inbox-live-blink 1.3s ease-in-out infinite;
}

@keyframes inbox-live-blink {
    0%,
    100% {
        opacity: 1;
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.55);
    }
    50% {
        opacity: 0.2;
        box-shadow: 0 0 0 5px rgba(255, 255, 255, 0);
    }
}

.inbox-refresh-btn {
    border-radius: 20px;
    font-weight: 500;
    letter-spacing: 0.3px;
    transition: background-color 0.15s ease;
}

.inbox-refresh-btn:hover {
    background-color: rgba(255, 255, 255, 0.15) !important;
}

.inbox-canvas {
    background: #fff;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    padding: 0;
}

.inbox-canvas > .inbox-chip-row,
.inbox-canvas > .inbox-filter-bar,
.inbox-canvas > .v-alert,
.inbox-canvas > .v-progress-linear {
    flex: 0 0 auto !important;
}

.inbox-panel {
    background: #fff;
    border: none;
    border-bottom: 1px solid #dbe0e4;
    border-radius: 0;
    padding: 8px 12px;
}

.inbox-chip-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 8px;
    row-gap: 6px;
    overflow: visible;
}

.inbox-chip-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    min-width: 0;
}

.inbox-chip-group--types {
    flex: 1 0 auto;
    flex-wrap: nowrap;
    padding-left: 8px;
    border-left: 1px solid #cfd8dc;
}

.inbox-chip {
    cursor: pointer;
    margin: 2px 6px 2px 0;
}

.inbox-filter-bar {
    border: none !important;
    border-bottom: 1px solid #dbe0e4 !important;
    border-radius: 0;
    background: #fff;
    box-shadow: none !important;
}

.inbox-filter-field .v-input__slot {
    background: #f4f6f8 !important;
}

.inbox-filter-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.inbox-filter-field {
    flex: 1 1 140px;
    min-width: 130px;
}

.inbox-filter-field--search {
    flex: 2.2 1 200px;
    min-width: 180px;
}

.inbox-filter-field--assigned {
    flex: 1.9 1 190px;
    min-width: 170px;
}

.inbox-filter-field--assigned .v-select__selection {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: inline-block;
}

.inbox-filter-field--daterange {
    flex: 1.7 1 200px;
    min-width: 180px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.inbox-filter-field--daterange .v-input {
    flex: 1 1 auto;
    min-width: 0;
}

.inbox-filter-daterange-sep {
    color: #b0bec5;
    flex: 0 0 auto;
}

.inbox-filter-field .v-input {
    border-radius: 6px;
}

.inbox-filter-field .v-input__slot {
    box-shadow: 0 0 0 1px #d6dbe0 inset !important;
}

@media (max-width: 1500px) {
    .inbox-chip-group--types {
        flex-basis: 100%;
        flex-wrap: wrap;
        margin-left: 0;
        padding-left: 0;
        border-left: none;
    }

    .communications-inbox {
        padding: 8px 10px;
    }

    .inbox-panel {
        padding: 6px 10px;
    }

    .inbox-filter-bar.pa-2 {
        padding: 6px 8px !important;
    }
}

@media (max-height: 850px) {
    .communications-inbox {
        padding: 6px 10px;
    }

    .inbox-header {
        padding-top: 4px !important;
        padding-bottom: 4px !important;
        font-size: 15px;
    }

    .inbox-panel {
        padding: 6px 10px;
    }

    .inbox-filter-bar.pa-2 {
        padding: 4px 8px !important;
    }

    .inbox-filter-toolbar {
        gap: 6px;
    }

    /* Give the message list/detail area more room on short laptop screens
       by trimming the sticky account/registration/action panel above it.
       These use the ".communications-inbox" prefix so they reliably win
       over the unconditional rules declared later in this stylesheet. */
    .communications-inbox .inbox-detail-sticky {
        max-height: 42vh;
    }

    .communications-inbox .inbox-account-box {
        padding: 8px 12px 7px;
    }

    .communications-inbox .inbox-account-name-en {
        font-size: 15px;
    }

    .communications-inbox .inbox-account-meta {
        font-size: 11px;
        margin-top: 1px;
    }

    .communications-inbox .inbox-link-box,
    .communications-inbox .inbox-reg-box {
        margin: 6px 10px 4px;
        padding: 8px 10px 8px;
    }

    .communications-inbox .inbox-reg-header {
        margin-bottom: 5px;
    }

    .communications-inbox .inbox-reg-students {
        margin-bottom: 5px;
    }

    .communications-inbox .inbox-reg-balance-row {
        gap: 12px;
        padding-top: 6px;
    }

    .communications-inbox .inbox-action-bar {
        padding: 6px 12px 8px;
    }

    .communications-inbox .inbox-status-row {
        margin-bottom: 4px;
    }

    .communications-inbox .inbox-action-row {
        gap: 6px;
    }

    .communications-inbox .inbox-detail-scroll {
        padding: 10px 12px;
    }

    .communications-inbox .inbox-list-count {
        padding: 3px 12px;
    }

    .communications-inbox .inbox-row-grid--header {
        padding: 5px 12px;
    }

    .communications-inbox .inbox-item {
        padding: 6px 12px;
    }

    .communications-inbox .inbox-reply-box {
        padding: 8px 10px;
    }
}

@media (max-width: 1366px) {
    /* Narrower list/detail columns still need the account chip and the
       assign-to-user field to stay usable without pushing buttons off.
       Prefixed for the same specificity reason as above. */
    .communications-inbox .inbox-from-inline {
        max-width: 360px;
    }

    .communications-inbox .inbox-assign-autocomplete {
        max-width: 140px;
        min-width: 110px;
        flex: 1 1 110px;
    }

    .communications-inbox .inbox-row-grid {
        grid-template-columns: 24px 24px 1fr 80px 28px 64px;
        column-gap: 6px;
    }

    .communications-inbox .inbox-row-date {
        font-size: 11px;
    }

    /* Give the account name the room it needs on narrow screens: drop the
       Hebrew name from the compact list row (still shown in the detail
       panel) and let the English name wrap to two lines instead of
       truncating to a handful of characters. */
    .communications-inbox .inbox-row-name-he {
        display: none;
    }

    .communications-inbox .inbox-row-name {
        align-items: flex-start;
    }

    .communications-inbox .inbox-row-name-en {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        line-height: 1.25;
        word-break: break-word;
    }

    .communications-inbox .inbox-row-assigned .v-chip {
        font-size: 10px !important;
        height: 20px !important;
    }

    .communications-inbox .inbox-row-count {
        font-size: 11px;
    }
}

@media (max-width: 700px) {
    .inbox-chip-group,
    .inbox-chip-group--types {
        flex-basis: 100%;
        flex-wrap: wrap;
    }
}

.inbox-split {
    flex: 1 1 auto !important;
    min-height: 0;
    overflow: hidden;
    flex-wrap: nowrap !important;
    align-items: stretch !important;
    align-content: stretch !important;
    margin: 0 !important;
}

.inbox-split > .flex {
    padding: 0 !important;
}

.inbox-split-list,
.inbox-split-detail {
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: 100%;
    align-self: stretch;
}

.inbox-split-list {
    padding-right: 0;
    height: 100%;
    border-right: 1px solid #dbe0e4;
}

.inbox-split-detail {
    padding-left: 0;
    height: 100%;
}

.inbox-detail-empty {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: #fff;
    color: #78909c;
    border-radius: 0;
}

.inbox-list-card {
    border: none;
    border-radius: 0;
    background: #fff;
    overflow: hidden;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.inbox-row-grid {
    display: grid;
    grid-template-columns: 30px 30px 1fr 104px 40px 78px;
    column-gap: 8px;
    align-items: center;
}

.inbox-row-checkbox {
    display: flex;
    justify-content: center;
}

.inbox-row-checkbox .v-input--selection-controls {
    margin: 0;
    padding: 0;
}

.inbox-row-checkbox .v-input--selection-controls__ripple {
    left: -12px;
}

.inbox-bulk-bar {
    background: #e2f0e3;
    border: 1px solid #a5d6a7;
    border-radius: 6px;
    padding: 6px 10px;
    flex: 0 0 auto !important;
}

.inbox-bulk-bar > * {
    margin-right: 8px;
}

.inbox-bulk-count {
    font-size: 13px;
    font-weight: 600;
    color: #2e7d32;
}

.inbox-complete-card {
    overflow: hidden;
}

.inbox-complete-header {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    background: #4caf50;
    color: #fff;
}

.inbox-complete-title {
    font-size: 18px;
    font-weight: 600;
}

.inbox-complete-body {
    padding: 16px 20px 4px;
}

.inbox-complete-list {
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #eceff1;
    border-radius: 6px;
}

.inbox-complete-item {
    display: flex;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #eceff1;
}

.inbox-complete-item:last-child {
    border-bottom: none;
}

.inbox-complete-item-icon {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
}

.inbox-complete-item-body {
    flex: 1 1 auto;
    min-width: 0;
}

.inbox-complete-item-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.inbox-complete-item-chip {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    height: 20px;
}

.inbox-complete-item-name {
    font-weight: 600;
    font-size: 14px;
    color: #263238;
    white-space: normal;
    word-break: break-word;
}

.inbox-complete-item-name-he {
    font-size: 13px;
    color: #78909c;
}

.inbox-complete-item-date {
    margin-left: auto;
    font-size: 11px;
    color: #90a4ae;
    white-space: nowrap;
}

.inbox-complete-item-account {
    margin-top: 4px;
    font-size: 12px;
    color: #607d8b;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.inbox-complete-item-account--missing {
    color: #fb8c00;
    font-weight: 600;
}

.inbox-complete-item-message {
    margin-top: 8px;
    padding: 8px 10px;
    background: #f6f7f8;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.4;
    color: #37474f;
    white-space: pre-line;
    word-break: break-word;
}

.inbox-complete-item-fax {
    margin-top: 8px;
    max-width: none;
    padding: 0;
}

.inbox-complete-item-fax .fax-preview-frame {
    height: 320px;
}

.inbox-complete-actions {
    margin: 0 20px 16px;
    padding: 0;
}

.inbox-list-count {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 12px;
    background: #eceff1;
    font-size: 11px;
    font-weight: 600;
    color: #546e7a;
    border-bottom: 1px solid #cfd6dc;
    letter-spacing: 0.02em;
}

.inbox-list-count-range {
    font-weight: 500;
    color: #78909c;
    white-space: nowrap;
}

.inbox-list-count-value {
    margin-left: auto;
}

.inbox-list-count-more {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
    color: #90a4ae;
}

.inbox-row-grid--header {
    padding: 8px 12px;
    background: #dfe4e8;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #546e7a;
    letter-spacing: 0.03em;
    border-bottom: 1px solid #cfd6dc;
}

.inbox-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.inbox-item {
    cursor: pointer;
    border: none;
    border-bottom: 1px solid #eceff1;
    border-radius: 0;
    box-shadow: none;
    padding: 8px 12px;
}

.inbox-item:hover {
    background: #f1f4f6;
}

.inbox-item--active {
    background: #f1ffdc;
    box-shadow: inset 3px 0 0 #4caf50;
}

.inbox-row-type {
    display: flex;
    justify-content: center;
    position: relative;
}

.inbox-row-completed-icon {
    position: absolute;
    bottom: -6px;
    right: -2px;
    font-size: 13px !important;
    background: #fff;
    border-radius: 50%;
}

.inbox-row-account {
    min-width: 0;
}

.inbox-row-name {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
}

.inbox-row-name-en {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inbox-row-name-he {
    color: #78909c;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inbox-row-preview {
    font-size: 12px;
    color: #90a4ae;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inbox-row-assigned {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
}

.inbox-row-time {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.25;
    text-align: right;
    white-space: nowrap;
}

.inbox-row-date {
    font-size: 12px;
    font-weight: 600;
    color: #37474f;
}

.inbox-row-clock {
    font-size: 11px;
    font-weight: 500;
    color: #546e7a;
}

.inbox-detail {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
}

.inbox-detail-sticky {
    flex: 0 0 auto;
    max-height: 55vh;
    overflow-y: auto;
    border-bottom: 1px solid #cfd6dc;
    background: #f4f6f8;
}

.inbox-detail-sticky--collapsed {
    max-height: none;
}

.inbox-detail-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    background: #fff;
}

.inbox-account-box {
    padding: 12px 16px 10px;
    background: #37474f;
    border-bottom: 3px solid #4caf50;
}

.inbox-account-box-inner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.inbox-account-box-info {
    min-width: 0;
    flex: 1 1 auto;
}

.inbox-account-box-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
    flex: 0 0 auto;
}

.inbox-context-toggle {
    margin: 0;
    color: #eceff1 !important;
    border-color: #90a4ae !important;
}

.inbox-unlink-btn {
    flex: 0 0 auto;
    color: #ffab91 !important;
    border-color: #ffab91 !important;
    margin: 0;
}

.inbox-account-box--missing {
    background: #5d4037;
    border-bottom-color: #ff9800;
}

.inbox-account-name {
    display: flex;
    align-items: center;
}

.inbox-account-name-en {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
}

.inbox-account-name-he {
    margin-left: 8px;
    color: #cfd8dc;
}

.inbox-account-meta {
    font-size: 12px;
    color: #b0bec5;
    margin-top: 2px;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    line-height: 1.35;
}

.inbox-account-address {
    min-width: 0;
}

.inbox-account-address--collapsed {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inbox-account-missing-text {
    color: #ffcc80;
    display: flex;
    align-items: center;
}

.inbox-link-box {
    margin: 10px 12px 4px;
    padding: 10px 12px 12px;
    background: #fff8e1;
    border: 1px solid #ffb74d;
    border-left: 4px solid #fb8c00;
    border-radius: 6px;
}

.inbox-link-title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #e65100;
    margin-bottom: 6px;
}

.inbox-link-hint {
    margin-left: 8px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
    color: #ef6c00;
}

.inbox-link-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.inbox-link-lookup {
    flex: 1 1 0;
    min-width: 0;
}

.inbox-link-btn {
    flex: 0 0 auto;
    align-self: flex-start;
    margin-top: 18px;
}

.inbox-link-required {
    font-size: 13px;
    font-weight: 600;
    color: #e65100;
}

.inbox-row-attach {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 600;
    color: #607d8b;
}

.inbox-reg-box {
    margin: 10px 12px 4px;
    padding: 10px 12px 12px;
    background: #fff;
    border: 1px solid #a5d6a7;
    border-left: 4px solid #4caf50;
    border-radius: 6px;
}

.inbox-reg-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 8px;
}

.inbox-detail-sticky--collapsed .inbox-reg-header {
    margin-bottom: 0;
}

.inbox-detail-sticky--collapsed .inbox-reg-box {
    padding-bottom: 8px;
}

.inbox-reg-collapsed-hint {
    margin-left: 8px;
    font-size: 11px;
    font-weight: 700;
    color: #ef6c00;
}

.inbox-reg-title {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #2e7d32;
    margin-right: 8px;
}

.inbox-comm-count {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    color: #546e7a;
    white-space: nowrap;
    background: none;
    border: none;
    padding: 0;
}

.inbox-comm-count--link {
    cursor: pointer;
    color: #2e7d32;
    background: #e8f5e9;
    border: 1px solid #c8e6c9;
    border-radius: 14px;
    padding: 3px 8px 3px 6px;
    gap: 5px;
    text-decoration: none;
}

.inbox-comm-count--link:hover {
    background: #c8e6c9;
}

.inbox-comm-count-num {
    font-weight: 700;
}

.inbox-comm-count-period {
    color: #689f38;
    background: #fff;
    border-radius: 8px;
    padding: 0 5px;
    font-size: 10px;
    line-height: 16px;
}

.inbox-comm-count--grid {
    cursor: pointer;
    justify-content: center;
    min-width: 28px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    background: #eceff1;
    color: #37474f;
}

.inbox-row-count {
    display: flex;
    justify-content: center;
}

.inbox-history-card {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
}

.inbox-history-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 14px 12px 10px 16px;
    background: #4caf50;
    color: #fff;
}

.inbox-history-title {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
}

.inbox-history-title-period {
    margin-left: 6px;
    font-size: 12px;
    font-weight: 500;
    opacity: 0.85;
}

.inbox-history-account {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 500;
}

.inbox-history-account span {
    margin-left: 6px;
    opacity: 0.8;
    font-weight: 400;
}

.inbox-history-header .v-btn {
    color: #fff;
    margin-top: -4px;
}

.inbox-history-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px 16px;
    background: #f4f6f8;
    border-bottom: 1px solid #eceff1;
}

.inbox-history-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 10px;
    background: #eceff1;
    color: #455a64;
    font-size: 11px;
    font-weight: 600;
}

.inbox-history-chip--sms {
    background: #e8eaf6;
    color: #3949ab;
}

.inbox-history-chip--callback {
    background: #fff8e1;
    color: #f9a825;
}

.inbox-history-chip--email {
    background: #e0f2f1;
    color: #00897b;
}

.inbox-history-chip--fax {
    background: #efebe9;
    color: #6d4c41;
}

.inbox-history-chip--voicemail {
    background: #ede7f6;
    color: #5e35b1;
}

.inbox-history-chip--open {
    background: #fff8e1;
    color: #f9a825;
}

.inbox-history-toolbar {
    flex: 0 0 auto !important;
    padding: 4px 12px;
    margin: 0 !important;
    background: #e8f5e9;
    border-bottom: 1px solid #c8e6c9;
}

.inbox-history-toolbar-label {
    font-size: 13px;
    font-weight: 600;
    color: #2e7d32;
}

.inbox-history-check {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 32px;
    width: 32px;
    min-height: 32px;
}

.inbox-history-check .v-input--selection-controls {
    margin: 0;
    padding: 0;
}

.inbox-history-check .v-input--selection-controls__ripple {
    left: -12px;
}

.inbox-history-complete {
    flex: 0 0 auto;
    padding: 12px 16px 14px;
    border-top: 1px solid #c8e6c9;
    background: #f1f8e9;
}

.inbox-history-complete-title {
    font-size: 14px;
    font-weight: 600;
    color: #2e7d32;
}

.inbox-history-complete-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
}

.inbox-history-complete-actions .v-btn {
    margin-left: 8px;
}

.inbox-history-list {
    max-height: 65vh;
    overflow-y: auto;
    min-height: 120px;
    flex: 1 1 auto;
}

.inbox-history-row {
    border-bottom: 1px solid #eceff1;
}

.inbox-history-row:last-child {
    border-bottom: none;
}

.inbox-history-row--open {
    background: #fafbfb;
}

.inbox-history-row-header {
    display: flex;
    align-items: flex-start;
    padding: 10px 16px;
    cursor: pointer;
}

.inbox-history-row-header:hover {
    background: #f5f7f8;
}

.inbox-history-row-main {
    min-width: 0;
    flex: 1 1 auto;
}

.inbox-history-row-title {
    font-size: 13px;
    font-weight: 500;
    color: #37474f;
    display: flex;
    align-items: center;
}

.inbox-history-row-preview {
    font-size: 12px;
    color: #78909c;
    margin-top: 2px;
}

.inbox-history-row-meta {
    flex: 0 0 auto;
    text-align: right;
    font-size: 12px;
    color: #607d8b;
    margin: 0 8px;
}

.inbox-history-row-body {
    padding: 0 16px 14px 16px;
}

.inbox-history-chevron {
    flex: 0 0 auto;
    margin-top: 1px;
    color: #b0bec5 !important;
}

.inbox-history-attach {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    color: #607d8b;
}

.inbox-history-expand {
    padding: 0 16px 14px 84px;
}

.inbox-history-expand-text {
    font-size: 13px;
    line-height: 1.45;
    color: #37474f;
    white-space: pre-line;
    word-break: break-word;
    background: #f6f7f8;
    border-radius: 6px;
    padding: 8px 10px;
}

.inbox-history-expand .fax-preview,
.inbox-history-expand .email-body,
.inbox-detail-scroll .fax-preview,
.inbox-detail-scroll .email-body {
    max-width: none;
    padding: 0;
}

.inbox-history-expand .fax-preview-frame,
.inbox-history-expand .email-html-frame {
    height: 360px;
}

.inbox-history-typeicon {
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    margin-top: 1px;
}

.inbox-history-body {
    min-width: 0;
    flex: 1 1 auto;
}

.inbox-history-top {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
}

.inbox-history-date {
    color: #78909c;
    font-size: 12px;
    white-space: nowrap;
    margin-left: auto;
}

.inbox-history-preview {
    margin-top: 2px;
    font-size: 12px;
    color: #546e7a;
}

.inbox-history-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    font-size: 11px;
    color: #78909c;
}

.inbox-history-note {
    margin-top: 6px;
    font-size: 12px;
    color: #37474f;
    background: #f7f9fa;
    border-radius: 4px;
    padding: 4px 8px;
}

.inbox-balance-btn .v-btn__content {
    align-items: center;
}

.inbox-balance-btn .v-icon {
    font-size: 16px !important;
    height: 16px;
    width: 16px;
    line-height: 16px;
    margin-right: 6px;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
}

.inbox-reg-students {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
}

.inbox-reg-loading {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #78909c;
}

.inbox-student-chip {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 500;
    color: #37474f;
    background: #f1f8f2;
    border: 1px solid #c8e6c9;
    border-radius: 12px;
    padding: 3px 10px 3px 6px;
    cursor: pointer;
}

.inbox-student-chip:hover,
.inbox-student-chip:focus {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
    outline: none;
}

.inbox-student-chip--missing {
    background: #ffebee;
    border-color: #ef9a9a;
    color: #c62828;
}

.inbox-student-chip--skipcard {
    border-color: #ffcc80;
}

.inbox-student-class {
    color: #78909c;
    margin-left: 3px;
    font-weight: 400;
}

.inbox-student-applicant {
    color: #ef6c00;
    margin-left: 4px;
    font-size: 11px;
    font-weight: 600;
}

.inbox-student-skip-icon {
    margin-left: 4px;
    font-size: 14px !important;
    height: 14px;
    width: 14px;
}

.inbox-student-card {
    overflow: hidden;
}

.inbox-student-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px 8px 14px;
    background: #4caf50;
    color: #fff;
}

.inbox-student-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
}

.inbox-student-subtitle {
    margin-top: 1px;
    font-size: 12px;
    font-weight: 500;
}

.inbox-student-header .v-btn {
    color: #fff;
    margin: 0;
}

.inbox-student-body {
    padding: 10px 14px 4px;
}

.inbox-student-status-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
}

.inbox-student-status-row .v-chip {
    height: 22px !important;
}

.inbox-student-skipcard {
    display: inline-flex;
    align-items: center;
    flex: 1 1 240px;
    min-width: 0;
    background: #fff3e0;
    border: 1px solid #ffcc80;
    border-radius: 4px;
    color: #e65100;
    font-size: 12px;
    line-height: 1.3;
    padding: 3px 8px;
}

.inbox-student-skipcard-label {
    font-weight: 700;
    margin-right: 4px;
    flex: 0 0 auto;
}

.inbox-student-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 16px;
    border: 1px solid #eceff1;
    border-radius: 6px;
    padding: 4px 10px;
}

.inbox-student-detail-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 3px 0;
    border-bottom: 1px solid #f5f5f5;
    min-width: 0;
}

.inbox-student-detail-row:nth-last-child(-n+2) {
    border-bottom: none;
}

.inbox-student-detail-label {
    flex: 0 0 92px;
    font-size: 11px;
    font-weight: 600;
    color: #607d8b;
}

.inbox-student-detail-value {
    flex: 1 1 auto;
    font-size: 12px;
    color: #37474f;
    word-break: break-word;
}

.inbox-student-missing {
    margin-top: 8px;
}

.inbox-student-missing-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
}

.inbox-student-missing .inbox-reg-missing-item {
    padding: 3px 8px;
    margin: 0;
}

.inbox-student-actions {
    padding: 4px 10px 10px;
    margin: 0;
}

.inbox-reg-missing {
    margin-bottom: 8px;
}

.inbox-reg-missing-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    max-height: 130px;
    overflow-y: auto;
    padding-right: 4px;
}

.inbox-reg-missing-item {
    background: #ffebee;
    border-radius: 4px;
    padding: 4px 8px;
}

.inbox-reg-missing-title {
    font-size: 12px;
    font-weight: 600;
    color: #c62828;
}

.inbox-reg-missing-student {
    margin-right: 4px;
}

.inbox-reg-missing-desc {
    font-size: 11px;
    font-weight: 400;
    color: #8d4b4b;
    line-height: 1.3;
    margin-top: 2px;
}

.inbox-reg-balance-row {
    display: flex;
    align-items: center;
    gap: 20px;
    padding-top: 8px;
    border-top: 1px solid #eceff1;
}

.inbox-reg-balance-item {
    display: flex;
    flex-direction: column;
}

.inbox-balance-name {
    font-size: 14px;
    font-weight: 600;
    color: #37474f;
}

.inbox-balance-transactions {
    max-height: 340px;
    overflow-y: auto;
}

.inbox-reg-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #78909c;
    margin-bottom: 4px;
}

.inbox-reg-value {
    font-size: 13px;
    font-weight: 600;
    color: #37474f;
    line-height: 1.3;
}

.inbox-reg-value--warning {
    color: #c62828;
}

.inbox-action-bar {
    padding: 10px 16px 12px;
    overflow: visible;
}

.inbox-status-row {
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1;
    margin-bottom: 6px;
}

.inbox-status-row .v-chip {
    margin: 0;
}

.inbox-action-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    overflow: visible;
}

.inbox-content-toolbar {
    flex-wrap: wrap;
}

.inbox-related-btn {
    font-weight: 600;
    letter-spacing: 0.2px;
    box-shadow: 0 1px 3px rgba(21, 101, 192, 0.45);
}

.inbox-from-inline {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    color: #37474f;
    max-width: 520px;
}

.inbox-from-inline-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inbox-cc-inline-text {
    margin-left: 10px;
    max-width: 220px;
}

.inbox-email-meta-trigger {
    cursor: pointer;
    border-radius: 4px;
    margin-left: 2px;
    flex-shrink: 0;
}

.inbox-email-meta-trigger:hover {
    background: #f1f4f6;
}

.inbox-email-meta-card {
    font-size: 13px;
    line-height: 1.7;
}

.inbox-email-meta-item {
    color: #37474f;
}

.inbox-email-meta-label {
    display: inline-block;
    min-width: 62px;
    color: #90a4ae;
    font-weight: 500;
}

.inbox-user-menu {
    z-index: 300 !important;
}

.inbox-assign-label {
    font-size: 13px;
    font-weight: 500;
    color: #546e7a;
    flex: 0 0 auto;
}

.inbox-assign-autocomplete {
    max-width: 180px;
    min-width: 140px;
    flex: 1 1 140px;
}

.inbox-body {
    white-space: pre-line;
    line-height: 1.45;
}

.inbox-callback-layout {
    margin-left: -8px;
    margin-right: -8px;
}

.inbox-callback-layout > .flex {
    padding-left: 8px;
    padding-right: 8px;
}

.inbox-callback-meta audio {
    width: 100%;
    max-width: 360px;
}

.inbox-callback-meta .voicemail-preview {
    padding: 0;
}

.inbox-callback-meta .voicemail-audio {
    max-width: 360px;
}

.inbox-callback-transcript .inbox-transcript {
    margin-top: 0;
}

.inbox-transcript {
    margin-top: 4px;
}

.inbox-transcript-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #78909c;
    margin-bottom: 4px;
}

.inbox-transcript-label--toggle {
    cursor: pointer;
    user-select: none;
}

.inbox-transcript-text {
    font-size: 14px;
    line-height: 1.45;
    color: #37474f;
    word-break: break-word;
    background: #f6f7f8;
    border-radius: 6px;
    padding: 8px 10px;
}

.inbox-transcript-text p,
.inbox-transcript-text ul,
.inbox-transcript-text ol {
    margin: 0 0 0.55em;
}

.inbox-transcript-text p:last-child,
.inbox-transcript-text ul:last-child,
.inbox-transcript-text ol:last-child {
    margin-bottom: 0;
}

.inbox-transcript-pending {
    font-size: 13px;
    color: #90a4ae;
}

.inbox-reply-box {
    margin-top: 4px;
    background: #f7fdf2;
    border: 1px solid #c8e6c9;
    border-radius: 6px;
    padding: 10px 12px;
}

.inbox-reply-header {
    font-size: 12px;
    font-weight: 600;
    color: #2e7d32;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
}

.inbox-reply-field {
    margin-top: 0;
    margin-bottom: 4px;
    background: #fff;
}

.inbox-reply-textarea {
    background: #fff;
}

.inbox-manage-files-dialog {
    margin: 24px auto !important;
    width: 88vw !important;
    max-width: 88vw !important;
}

.inbox-manage-files-card {
    height: 90vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.inbox-manage-files-card .files-page,
.inbox-manage-files-card .files-body,
.inbox-manage-files-card .files-body > .layout {
    width: 100%;
}

.inbox-manage-files-card .files-page {
    flex: 1;
    min-height: 0;
}

.inbox-manage-files-card .files-header,
.inbox-manage-files-card .files-account-strip {
    flex: 0 0 auto !important;
}

.inbox-manage-files-card .files-page-col {
    flex-basis: var(--col-width, 25%) !important;
    max-width: var(--col-width, 25%) !important;
}

.inbox-manage-files-close-copy {
    font-size: 14px;
    line-height: 1.4;
    color: #455a64;
}
</style>
